import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "sonner";

// Flash Tools: popups silenced (log keeps everything) — only DTC results use sonnerToast.
const noop = () => {};
const toast = Object.assign(noop, { success: noop, error: noop, warning: noop, info: noop, message: noop, loading: noop, dismiss: noop });
import { Info, FileDigit, ClipboardList, Link2, Download, Power, Upload, Cpu, ScanSearch, Eraser, RotateCcw, X, HardDriveDownload, MemoryStick, Hash, CheckCircle2, AlertTriangle, Copy, Trash2, Bike, FileText, Wrench, Gauge as GaugeIcon } from "lucide-react";
import { getSharedManager, runFlash, ecmIdFromBin, readDtc, clearDtc, resetEcm, readLiveData, parseLiveData, runRead, runReadEepromShindengen, runReadEepromKeihin, runClearCountShindengen, runClearCountKeihin, armKeihinR8CRead, runFormatEepromKeihin, runWriteEepromKeihin, runWriteEepromShindengen, runKawasakiZx25rWrite, runKawasakiZx25rInfo, runKawasakiZx25rUnlock, testUnlockDenso, probeReadDenso, probeReadDensoApp, readDensoApp } from "../flashtools/lib/serial";
import { parseFirmware, crc32 } from "../flashtools/lib/firmware";
import { dtcName } from "../flashtools/lib/hondaDtc";
import { setAdminEcuDb, lookupEcm } from "../flashtools/lib/ecmDatabase";
import { brandLog } from "../flashtools/lib/brand";
import { getEcuDatabase, saveFlashSession } from "../flashtools/lib/api";
import { SaharaReadPanel } from "../flashtools/components/SaharaReadPanel";
import { FlashResultDialog } from "../flashtools/components/FlashResultDialog";
import { ChecksumWarnDialog } from "../flashtools/components/ChecksumWarnDialog";
import { saveBinaryWithPicker } from "../flashtools/lib/saveAs";
import { getChecksumStatus, autoCorrectChecksum, shindengenChecksumAddr, listChecksums, looksLikeK2F256 } from "../lib/checksum";
import { fetchBridgePorts, BRIDGE_LOCK, markBridgeBusy, clearBridgeBusy } from "../flashtools/lib/bridge";
import { useAuth } from "../context/AuthContext";
import { RaceDash } from "../flashtools/components/RaceDash";

const BRIDGE_URL = "ws://localhost:8765";
const IS_TOUCH = typeof window !== "undefined" && (
  (typeof navigator !== "undefined" && /TiroTunerApp/i.test(navigator.userAgent || "")) ||
  (!!window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
  (typeof navigator !== "undefined" && (navigator.maxTouchPoints || 0) > 0)
);
const CompactCtx = createContext(false);
const BAUD = 10400;
const SIZES_SHINDENGEN = ["256 KB", "384 KB"];
const SIZES_DENSO = ["384 KB"];
const SIZES_KEIHIN = ["48 KB", "56 KB", "64 KB", "96 KB", "128 KB", "256 KB", "384 KB", "512 KB", "1024 KB"];
const ALL_SIZES = ["256 KB", "384 KB", "48 KB", "56 KB", "64 KB", "96 KB", "128 KB", "512 KB", "1024 KB"];
const SIZE_BYTES = { "48 KB": 49152, "56 KB": 57344, "64 KB": 65536, "96 KB": 98304, "128 KB": 131072, "256 KB": 262144, "384 KB": 393216, "512 KB": 524288, "1024 KB": 1048576 };
const dbFlashToLabel = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  if (/kb|mb/i.test(s)) {
    const m = s.replace(/\s+/g, " ").toUpperCase();
    return ALL_SIZES.find((x) => x.toUpperCase() === m) || "";
  }
  const n = parseInt(s, 10);
  if (!n) return "";
  if (n >= 100000) return Object.keys(SIZE_BYTES).find((k) => SIZE_BYTES[k] === n) || "";
  if (n === 1024) return "1024 KB";
  const lbl = `${n} KB`;
  return ALL_SIZES.includes(lbl) ? lbl : "";
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const COLD_TXT = { ig_off: "🔻 MATIKAN IGNITION (OFF)", arming: "⏳ TAHAN IGNITION OFF", ig_on: "🔑 NYALAKAN IGNITION (ON) SEKARANG!", reconnect: "🔌 Menyambung port baru di power-on…", prep: "⏳ TAHAN IGNITION OFF — menyiapkan adapter…" };
const now = () => new Date().toTimeString().slice(0, 8);

const ColdGuideBanner = ({ phase, seconds }) => (
  <div className="rounded-lg border-2 border-amber-400/50 bg-amber-500/10 px-4 py-3 flex items-center gap-3" data-testid="tools-coldguide">
    <span className="text-amber-300 font-bold text-[16px]">{COLD_TXT[phase] || "Cold read…"}</span>
    {phase === "arming" && <span className="ml-auto text-3xl font-mono font-bold text-amber-300" data-testid="tools-coldguide-sec">{seconds}s</span>}
  </div>
);

// Reusable labeled input row for the INFO panels.
const Row = ({ label, value, onChange, readOnly = true, redLabel = false, testid, placeholder }) => {
  const compact = useContext(CompactCtx);
  return (
  <div className="flex items-center gap-3">
    <span className={`w-36 shrink-0 ${compact ? "text-[13px]" : "text-[16px]"} font-medium ${redLabel ? "text-red-600" : "text-white"}`}>{label}</span>
    <input
      data-testid={testid}
      value={value ?? ""}
      readOnly={readOnly}
      placeholder={placeholder}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      className={`flex-1 min-w-0 ${compact ? "h-7 text-[13px]" : "h-8 text-[14px]"} px-3 rounded-[3px] border border-[#6b7280] bg-[#16181d] text-white font-mono outline-none focus:border-red-500 ${readOnly ? "opacity-90" : ""}`}
    />
  </div>
  );
};

// Denso READ is hidden until a working read protocol is found (probe code kept).
const SHOW_DENSO_READ = false;

const PanelHead = ({ icon, title, right }) => {
  const compact = useContext(CompactCtx);
  return (
  <div className="ft-panelhead relative flex items-stretch rounded-t-md overflow-hidden bg-[#3a0a0d] border-b border-red-800/60" style={{ backgroundImage: "url(/trmd-head.webp)", backgroundSize: "100% 100%" }}>
    <div className={`flex items-center gap-3 pl-5 ${compact ? "pr-9 py-1.5" : "pr-14 py-2"} bg-gradient-to-b from-[#ef2b2b] to-[#c51717]`} style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 24px) 100%, 0 100%)" }}>
      <span className="text-white flex items-center">{icon}</span>
      <h2 className={`text-white font-black tracking-wide uppercase ${compact ? "text-[16px]" : "text-[21px]"}`}>{title}</h2>
    </div>
    <div className="w-3 -ml-6 bg-red-500/50" style={{ clipPath: "polygon(70% 0, 100% 0, 30% 100%, 0 100%)" }} />
    {right ? <div className="ml-auto flex items-stretch">{right}</div> : <div aria-hidden className="ml-auto w-12 bg-red-600" style={{ clipPath: "polygon(100% 0, 100% 100%, 0 0)" }} />}
  </div>
  );
};

const TrmdBanner = ({ shift = false }) => {
  const compact = useContext(CompactCtx);
  return (
    <div className={`relative shrink-0 overflow-hidden bg-[#0a0b0e] ${compact ? "h-14" : "h-[80px]"} ${shift ? "pr-16" : ""}`} data-testid="honda-trmd-banner">
      <img src="/trmd-banner.webp" alt="" className="h-full w-full object-cover object-left select-none pointer-events-none" draggable={false} />
      <img src="/trmd-logo.webp" alt="TRMD" data-testid="honda-trmd-logo" draggable={false} className={`absolute ${shift ? "right-20" : "right-6"} top-1/2 -translate-y-1/2 w-auto object-contain select-none pointer-events-none max-w-[34%] ${compact ? "h-10" : "h-[60px]"}`} />
    </div>
  );
};

const TAB_ICONS = { shindengen: Cpu, tools: Wrench, dtc: ScanSearch, engine: GaugeIcon };

const BOTTOM_TABS = [
  { id: "shindengen", label: "REMAP" },
  { id: "tools", label: "TOOLS" },
  { id: "dtc", label: "SCAN DTC - RESET ECM" },
  { id: "engine", label: "ENGINE DATA" },
];

// Live sensor channels (from readLiveData/parseLiveData).
const SENSORS = [
  { key: "rpm", label: "Engine Speed - RPM", unit: "rpm", max: 14000, dec: 0, typical: "idle ~1500" },
  { key: "tps", label: "Throttle Position Sensor (TPS)", unit: "%", max: 100, dec: 0, typical: "0–100" },
  { key: "ect", label: "Engine Coolant Temp (ECT)", unit: "°C", max: 150, dec: 0, typical: "80–95" },
  { key: "iat", label: "Intake Air Temp (IAT)", unit: "°C", max: 100, dec: 0, typical: "20–60" },
  { key: "map", label: "Manifold Pressure (MAP)", unit: "kPa", max: 120, dec: 0, typical: "20–101" },
  { key: "battery", label: "Battery Voltage (V)", unit: "V", max: 18, dec: 1, typical: "12–14.5" },
  { key: "o2", label: "O2 Sensor (V)", unit: "V", max: 5, dec: 2, typical: "0.1–0.9" },
  { key: "injector", label: "Injector", unit: "ms", max: 30, dec: 2, typical: "1–16" },
  { key: "ignition", label: "Ignition Advance", unit: "°", max: 60, dec: 1, typical: "5–40" },
  { key: "speed", label: "Vehicle Speed", unit: "km/h", max: 200, dec: 0, typical: "0–160" },
];
const fmtV = (v, dec = 0) => (v == null || !Number.isFinite(v) ? "0" : v.toFixed(dec));

const LineChart = ({ series, yMax, color = "#2563eb" }) => {
  const W = 600, H = 260;
  const n = series.length;
  const pts = n > 1 ? series.map((v, i) => {
    const x = (i / (n - 1)) * W;
    const y = H - (Math.max(0, Math.min(yMax, v ?? 0)) / (yMax || 1)) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ") : "";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full min-h-[200px] bg-[#0c1017]">
      {[0, 0.25, 0.5, 0.75, 1].map((g) => <line key={g} x1={0} x2={W} y1={H * g} y2={H * g} stroke="#1f2937" strokeWidth="1" />)}
      {pts && <polyline points={pts} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
};

const ChartBox = ({ title, series, yMax, color }) => (
  <div className="flex flex-col h-full">
    {title && <div className="text-center font-bold text-white py-1 border-b border-[#2c2f36]">{title}</div>}
    <div className="flex flex-1 min-h-0">
      <div className="flex flex-col justify-between py-1 pr-1 text-[11px] font-mono text-slate-200 text-right w-10 h-full">
        {[1, 0.75, 0.5, 0.25, 0].map((t) => <span key={t}>{Math.round(yMax * t)}</span>)}
      </div>
      <div className="flex-1 border-l border-b border-[#6b7280] flex"><LineChart series={series} yMax={yMax} color={color} /></div>
    </div>
  </div>
);

const MMC = ({ min, setMin, current, max }) => (
  <div className="grid grid-cols-3 gap-2 mt-2 text-center text-[13px]">
    <div><div className="text-slate-400">Min</div><input value={min} onChange={(e) => setMin(e.target.value.replace(/[^0-9.]/g, ""))} className="w-full h-9 text-center rounded border border-slate-700 bg-[#121824] text-slate-100 font-mono" /></div>
    <div><div className="text-emerald-300 font-semibold">Current</div><div className="h-9 flex items-center justify-center rounded border border-white/10 bg-[#121824] font-mono font-bold">{current}</div></div>
    <div><div className="text-slate-400">Max</div><div className="h-9 flex items-center justify-center rounded border border-white/10 bg-[#121824] font-mono">{max}</div></div>
  </div>
);
const GAUGES = [
  { key: "rpm", short: "RPM", color: "#60a5fa" },
  { key: "tps", short: "TPS", color: "#4ade80" },
  { key: "ect", short: "ECT", color: "#f87171" },
  { key: "iat", short: "IAT", color: "#fbbf24" },
  { key: "map", short: "MAP", color: "#c084fc" },
  { key: "battery", short: "BAT", color: "#22d3ee" },
  { key: "o2", short: "O2", color: "#f472b6" },
  { key: "speed", short: "SPD", color: "#ffffff" },
];
const Gauge = ({ label, value, unit, color }) => (
  <div className="rounded-md border-2 border-[#4b505b] bg-[#16181d] flex flex-col items-center justify-center py-3 min-h-[76px]" data-testid={`gauge-${label.toLowerCase()}`}>
    <div className="text-[12px] font-bold text-white uppercase tracking-wider">{label}</div>
    <div className="text-[26px] font-bold font-mono leading-none mt-1" style={{ color }}>{value}</div>
    <div className="text-[12px] text-slate-200 mt-0.5">{unit}</div>
  </div>
);
// Prefix a log line with its operation (read/write) for clearer console output.
function fmtLogMsg(l) {
  const msg = (l && l.msg) || "";
  if (l && l.level === "READ" && !/^\s*(read|eeprom|verify|rd\b)/i.test(msg)) return "read " + msg;
  if (l && l.level === "WRITE" && /^\s*(0x|block\b|clear\b)/i.test(msg)) return "write " + msg;
  return msg;
}


// Map a log (level + message) to a short process label + real progress % for the status bar.
function phaseFromLog(level, msg) {
  const m = (msg || "");
  const low = m.toLowerCase();
  const pm = m.match(/\((\d+)%\)/);
  const pct = pm ? Number(pm[1]) : null;
  // Terminal states first (regardless of level).
  if (/read complete|cold read complete|read.*bytes in/i.test(low)) return { label: "READ selesai ✓", pct: 100 };
  if (/good result|berhasil|selesai ✓|verified ✓|image written|programmed in|flash finished|state 0x0f \(committed\)/i.test(low)) return { label: "WRITE selesai ✓ GOOD", pct: 100 };
  if (/result is bad|\bbad\b|failed|gagal|rejected|mismatch/i.test(low)) return { label: "GAGAL ✗" };
  if (/flash session start|^protocol:|^checksum @|init-write|opening programming|^init |^wake:|session open|erasing flash|erase command|^erase-|^bulk erase|erase confirmed|^state poll|^range:|^cmd-|^arm:|building .*write frames|fast batch/i.test(low)) return { label: "INITIAL…", pct: /flash session start/i.test(low) ? 0 : undefined };
  switch (level) {
    case "WRITE":
      if (/all .*blocks written|write complete|blocks written/i.test(low)) return { label: "WRITE complete", pct: pct ?? 100 };
      return { label: "WRITE…", pct };
    case "ERASE":
      if (/complete|erased/i.test(low)) return { label: "Erasing complete ✓" };
      return { label: "Erasing…" };
    case "READ":
      return { label: "READ…", pct };
    default: break;
  }
  // INFO / SUCCESS / WARN — detect by message.
  if (/finalizing|committing/i.test(low)) return { label: "Finalizing…" };
  if (/computed .*key|seed-key|seed\/key|security access|security granted/i.test(low)) return { label: "Handshake / Seed-Key…" };
  if (/waking bus|wakeup|probing ecu/i.test(low)) return { label: "Handshake…" };
  if (/flash count:/i.test(low)) return { label: "Reading ECU…" };
  if (/connected:/i.test(low)) return { label: "Connected ✓" };
  if (/native bridge.*connected/i.test(low)) return { label: "Bridge connected…" };
  if (/connecting via native bridge/i.test(low)) return { label: "Connecting…" };
  if (/flash session start/i.test(low)) return { label: "Starting…", pct: 0 };
  return null;
}


// Protocol/command lines that must stay hidden from normal users (anti-cloning).
// Admin can enable the "full-log" feature per user to reveal these.
function isSensitiveLog(entry) {
  if (!entry) return false;
  if (entry.level === "UART") return true;
  const m = entry.msg || "";
  return /\bTX\b|\bRX\b|seed|\bkey\b|security\s+access|security\s+accepted|engine\s+open|challenging|preamble|handshake|write\s+base|block\s+addr|program\s+base|blank[\s-]?skip|contiguous|identity[\s-]?mapped|mcu\s+tag|sv850|sh850|honda\s+xor|native\s+batch|streaming|waking\s+bus|wake\s*up|\bwake\b|melewati|byte 0\.\.0x|auto\s+offsets|@\s*0x[0-9a-f]|addr16|write\s+frames?|write\s+protocol|routing\s+to|image\s+signature|settl(?:e|ing)|reference\s+tool|session\s+open|terminator|finalize:|(?:[0-9a-f]{2}\s+){2,}[0-9a-f]{2}|\b0x[0-9a-f]+|\bfmt\d|probe|address\s+format|read-back|words?\s+\d|bootloader|62500|baud|state\s+poll|\bcmd-|\bfin-|erase-\d|bulk\s+erase|\barm:|range:|header\s+rebuilt|C=|block\s+\d+B|\d+B\)|32-bit|sparse|0xff\s+blocks|empty\s+0xff/i.test(m);
}




// Read-only console: "write" highlighted orange, "read" highlighted blue.
const LOG_WORD = /\b(write|read)\b/i;
const LogLine = ({ line }) => line.split(/(\bwrite\b|\bread\b)/i).map((p, i) =>
  LOG_WORD.test(p) && p.length <= 5
    ? <span key={i} className="font-bold">{p}</span>
    : p);
const LEVEL_CLS = {};
const LogView = ({ boxRef, lines, empty, testid, className }) => (
  <div ref={boxRef} data-testid={testid} data-no-i18n
    className={`w-full h-full overflow-auto whitespace-pre-wrap break-words border border-[#4b505b] bg-[#0c1017] text-white text-[13px] font-mono p-3 pr-24 select-text ${className}`}>
    {lines.length ? lines.map((l, i) => <div key={i} data-level={l.level} className={LEVEL_CLS[l.level] || ""}><LogLine line={l.text} /></div>) : <div>{empty}</div>}
  </div>
);


export function HondaFlashtoolsPanel({ embedded = false, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeFullLog = !!user?.is_admin || (Array.isArray(user?.features) && user.features.includes("full-log"));
  const manager = getSharedManager();
  const fileRef = useRef(null);
  const logBoxRef = useRef(null);
  const toolsLogRef = useRef(null);

  const [logs, setLogs] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [flashing, setFlashing] = useState(false);
  const [device, setDevice] = useState(null);
  const [firmware, setFirmware] = useState(null);
  const [startOffset, setStartOffset] = useState("0");
  const [checksumOffset, setChecksumOffset] = useState("0");
  const [flashSize, setFlashSize] = useState("");
  const [smartChecksum, setSmartChecksum] = useState(false);
  const [fastWrite, setFastWrite] = useState(true);
  const [progress, setProgress] = useState(0);
  const [flashResult, setFlashResult] = useState(null);
  const [csWarn, setCsWarn] = useState(null); // null | "load" | "write"
  const [pendingWrite, setPendingWrite] = useState(false);
  const [zxBusy, setZxBusy] = useState(false);
  const [speed, setSpeed] = useState(0);
  const [procStatus, setProcStatus] = useState("");
  const [recoveryKeyInput] = useState("");
  const [tab, setTab] = useState("shindengen");
  const [comPort, setComPort] = useState("");
  const [ports, setPorts] = useState([]);
  const [deviceType, setDeviceType] = useState("ftdi");
  const [dtcRows, setDtcRows] = useState([]);
  const [dtcBusy, setDtcBusy] = useState(false);
  const [livePoll, setLivePoll] = useState(false);
  const [sample, setSample] = useState({});
  const [hist, setHist] = useState({});
  const [peaks, setPeaks] = useState({});
  const [engineSel, setEngineSel] = useState("tps");
  const [engineView, setEngineView] = useState(() => localStorage.getItem("trmd_engine_view") || "dash");
  const switchEngineView = (v) => { setEngineView(v); localStorage.setItem("trmd_engine_view", v); };
  const [graphA, setGraphA] = useState("rpm");
  const [graphB, setGraphB] = useState("battery");
  const [engineMin, setEngineMin] = useState("0");
  const [graphMinA, setGraphMinA] = useState("2000");
  const [graphMinB, setGraphMinB] = useState("12");
  const [toolMaker, setToolMaker] = useState("Keihin");
  const [toolMode, setToolMode] = useState("read");
  const [readSize, setReadSize] = useState("256 KB");
  const [securityPw, setSecurityPw] = useState("");
  const [reading, setReading] = useState(false);
  const [densoMethod, setDensoMethod] = useState(null);
  const [eepromBytes, setEepromBytes] = useState(null);
  const [eepromName, setEepromName] = useState("");
  const [eepromSize, setEepromSize] = useState(512);
  const [saveDlg, setSaveDlg] = useState(null);
  const [saveNameSuffix, setSaveNameSuffix] = useState("");
  const eepromRef = useRef(null);
  const [coldGuide, setColdGuide] = useState({ open: false, phase: "", seconds: 0 });
  const sensorRows = SENSORS;
  const gaugeCards = GAUGES;
  const serialPorts = ports.filter((p) => p.kind === deviceType || p.kind === "usb"); // only real FTDI / CH340 / CP210x adapters (Android lists only USB-serial)
  const [confirmState, setConfirmState] = useState(null); // floating confirm: { message, resolve }
  const askConfirm = useCallback((message) => new Promise((resolve) => setConfirmState({ message, resolve })), []);
  const showInfo = useCallback((message) => setConfirmState({ message, info: true, resolve: () => {} }), []);
  const resolveConfirm = useCallback((val) => setConfirmState((c) => { if (c) c.resolve(val); return null; }), []);
  const pollRef = useRef(null);
  const compact = false; // same large layout as Yamaha on every device (Android full-screen is scaled by useFit)
  const renderDeviceControls = (portCls) => (
    <>
      <span className="text-[15px] font-semibold text-white shrink-0">Device</span>
      <select value={deviceType} onChange={(e) => setDeviceType(e.target.value)} disabled={connected || connecting || flashing}
        data-testid="honda-device-select"
        className={`shrink-0 rounded-[3px] border border-[#6b7280] bg-[#16181d] px-2 text-[14px] text-white outline-none focus:border-red-500 disabled:opacity-50 ${compact ? "h-8 w-24" : "h-9"}`}>
        <option value="ftdi">FTDI</option>
        <option value="ch340">CH340 (VAG KKL)</option>
        <option value="cp210x">CP210x</option>
        <option value="tactrix" disabled={IS_ANDROID}>Tactrix Openport{IS_ANDROID ? " (Windows saja)" : ""}</option>
        <option value="sm3" disabled={IS_ANDROID}>Scanmatik{IS_ANDROID ? " (Windows saja)" : ""}</option>
      </select>
      <span className="text-[15px] font-semibold text-white shrink-0">Port</span>
      <select value={comPort} onChange={(e) => setComPort(e.target.value)} disabled={connected || connecting || flashing}
        data-testid="honda-comport-select"
        className={`${portCls} rounded-[3px] border border-[#6b7280] bg-[#16181d] px-2 text-[14px] text-white outline-none focus:border-red-500 disabled:opacity-50 ${compact ? "h-8" : "h-9"}`}>
        {deviceType === "sm3" ? (
          <>
            <option value="sm3">SM3 USB</option>
            <option value="sm3wifi">SM3 Wi-Fi</option>
            <option value="sm2">SM2 USB</option>
          </>
        ) : deviceType === "tactrix" ? (
          <option value="openport">{(ports.find((p) => p.kind === "tactrix") || ports.find((p) => p.kind === "j2534") || {}).name || "Openport (J2534)"}</option>
        ) : (
          <>
            {serialPorts.length === 0 && <option value="" disabled>— (port tidak terdeteksi — klik Refresh) —</option>}
            {serialPorts.map((p) => <option key={p.port} value={p.port}>{p.port}{p.name && p.name !== p.port ? ` — ${p.name}` : ""}</option>)}
          </>
        )}
      </select>
      <button data-testid="honda-comport-refresh" onClick={loadPorts} disabled={connected || flashing}
        className={`rounded-[3px] border border-[#6b7280] bg-[#1d2027] hover:bg-[#2a2e37] text-white inline-flex items-center justify-center disabled:opacity-50 shrink-0 ${compact ? "h-8 w-8" : "h-9 w-9"}`}><RotateCcw size={16} /></button>
    </>
  );

  const log = useCallback((level, rawMsg) => {
    const st = phaseFromLog(level, rawMsg);
    let msg = brandLog(rawMsg);
    if (!canSeeFullLog && (level === "READ" || level === "WRITE" || /^re-read block /.test(msg))) msg = msg.replace(/0x[0-9A-Fa-f]+\s*:?\s*/g, "").trim();
    if (st) {
      if (st.label) setProcStatus(st.label);
      if (st.pct != null) setProgress(st.pct);
    }
    setLogs((prev) => [...prev.slice(-2000), { time: now(), level, msg }]);
    if (/Security OK/.test(rawMsg)) setColdGuide((g) => (g.phase === "ig_on" ? { open: false, phase: "", seconds: 0 } : g));
  }, [canSeeFullLog]);

  useEffect(() => {
    getEcuDatabase().then((list) => {
      if (list && list.length) { setAdminEcuDb(list); log("INFO", `Loaded ${list.length} ECU record(s) from admin database.`); }
    }).catch(() => {});
  }, [log]);

  useEffect(() => {
    if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    if (toolsLogRef.current) toolsLogRef.current.scrollTop = toolsLogRef.current.scrollHeight;
  }, [logs]);

  const clearLog = useCallback(() => { setLogs([]); toast.success("Log dibersihkan"); }, []);
  const copyLog = useCallback(async () => {
    const src = canSeeFullLog ? logs : logs.filter((l) => !isSensitiveLog(l));
    const text = src.map((l) => `[${l.time}] ${fmtLogMsg(l)}`).join("\n");
    try {
      if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); toast.success("Log disalin ke clipboard"); return; }
      throw new Error("no clipboard api");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        ok ? toast.success("Log disalin ke clipboard") : toast.error("Gagal menyalin log");
      } catch { toast.error("Gagal menyalin log"); }
    }
  }, [logs]);

  const humanSize = (n) => {
    if (!n) return "";
    if (n % (1024 * 1024) === 0) return `${n / (1024 * 1024)} MB`;
    return `${Math.round(n / 1024)} KB`;
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    try {
      const fw = await parseFirmware(file);
      // Shindengen 256KB: show the real dynamic checksum slot instead of the generic 3FFF8 default.
      if (fw.size === 0x40000) {
        const sh = shindengenChecksumAddr(new Uint8Array(fw.bytes));
        if (sh != null) { fw.checksumOffset = sh.toString(16).toUpperCase(); fw.chip = "Shindengen 256K"; }
        else {
          // Keihin 256KB: take the checksum slot from the ECM DB (K2F M16C = 9FEF, K93 SPC560 = 3FFF8).
          const db = ecmIdFromBin(fw.bytes) || ((device?.ecmId) ? lookupEcm(device.ecmId) : null);
          if (db && db.maker === "Keihin" && /^(9FEF|3FFF8)$/i.test(db.checksum || "")) {
            fw.checksumOffset = db.checksum.toUpperCase();
            fw.chip = `Keihin 256K${db.mcu ? " " + db.mcu : ""}`;
          } else if (!db && looksLikeK2F256(new Uint8Array(fw.bytes))) {
            fw.checksumOffset = "9FEF";
            fw.chip = "Keihin 256K K2F Renesas RH850";
          }
        }
      }
      setFirmware(fw);
      setStartOffset(fw.startOffset);
      setChecksumOffset(fw.checksumOffset);
      setFlashSize(humanSize(fw.size));
      log("INFO", `Loaded ${fw.name} (${fw.size} bytes, CRC32 ${fw.crc32})`);
      log("INFO", `Auto offsets → start 0x${fw.startOffset}, checksum 0x${fw.checksumOffset} [${fw.chip}]`);
      toast.success("Firmware loaded", { description: `${fw.name} · ${fw.chip}` });
      if (connected && (!device || !device.ecmId || device.ecmId === "-")) {
        const bi = ecmIdFromBin(fw.bytes);
        if (bi) { log("INFO", `ECM ID (from BIN): ${bi.ecmId} → ${bi.maker} ${bi.part}`); setDevice((d) => ({ ...(d || {}), ecmId: bi.ecmId, part: bi.part, maker: bi.maker, chip: bi.mcu, connection: d?.connection || "Recovery / Boot" })); }
      }
    } catch (e) {
      toast.error("Failed to parse firmware", { description: e.message });
    }
  }, [log, connected, device]);

  // BIN handoff from Map Editor "Flash Tools" / "Send to Flash" (via sessionStorage).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("tt_flash_handoff");
      if (!raw) return;
      sessionStorage.removeItem("tt_flash_handoff");
      const { name, b64 } = JSON.parse(raw);
      if (!b64) return;
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      handleFile(new File([bytes], name || "map.bin", { type: "application/octet-stream" }));
    } catch (_) {}
  }, [handleFile]);

  const handleConnect = useCallback(async () => {
    if (connected) {
      try { await manager.disconnect(); } catch (_) {}
      setConnected(false); setDevice(null); setProgress(0);
      log("INFO", "Serial port closed."); toast.info("Disconnected");
      return;
    }
    setConnecting(true);
    try {
      if (!comPort) {
        log("ERROR", "No COM port selected — click Refresh and pick one.");
        toast.error("Pilih Port dulu", { description: "Tidak ada mode Auto — pilih COM port adapter secara manual." });
        setConnecting(false);
        return;
      }
      log("INFO", deviceType === "sm3" ? `Connecting ${SM_NAMES[comPort] || "SM3 USB"} @ ${BAUD}...` : `Connecting ${deviceType.toUpperCase()} · ${comPort} @ ${BAUD}...`);
      await manager.connectBridge({ url: BRIDGE_URL, baud: BAUD, device: isJ(deviceType) ? undefined : comPort, dll: deviceType === "sm3" ? comPort : deviceType });
      log("SUCCESS", `Bridge v${manager.bridgeVersion || "?"} OK. Probing ECU...`);
      setConnected(true);
      const info = await manager.readDeviceInfoReal(log);
      setDevice(info);
      if (info.maker && /keihin/i.test(info.maker)) setToolMaker("Keihin");
      else if (info.maker && /shindengen/i.test(info.maker)) setToolMaker("Shindengen");
      else if (SHOW_DENSO_READ && info.maker && /denso/i.test(info.maker)) { setToolMaker("Denso"); setReadSize("384 KB"); }
      if (!firmware && info.startOffset) { setStartOffset(info.startOffset); setChecksumOffset(info.checksumOffset || "0"); }
      if (info.ecmId && info.ecmId !== "-" && !info.recovery) {
        log("SUCCESS", `Connected: ${info.ecmId} (${info.maker || "Unknown"})`);
        toast.success("ECM connected", { description: `${info.ecmId} · ${info.maker || ""} ${info.part || ""}`.trim() });
      } else if (info.recovery) {
        log("WARN", "⚠️ RECOVERY mode — load BIN, then WRITE.");
        toast.warning("ECU in Recovery");
      } else {
        log("ERROR", "No ECU detected — check ignition ON + wiring, then Connect again.");
        toast.error("No ECU detected");
        try { await manager.disconnect(); } catch (_) {}
        setConnected(false); setDevice(null);
      }
    } catch (e) {
      log("ERROR", /belum mendukung SM3|SM3 J2534 DLL|\[SM3 /.test(e.message) ? e.message : `Bridge offline — start TRMDEngine.exe (${e.message})`);
      toast.error("Connection failed", { description: e.message });
      setConnected(false);
    } finally {
      setConnecting(false);
    }
  }, [connected, manager, firmware, log, comPort, deviceType]);

  // Force disconnect: always available (connecting / read / WRITE) — confirm first, then abort + close.
  const forceDisconnect = useCallback(async () => {
    const busyWrite = flashing;
    const msg = busyWrite
      ? "⚠️ WRITE/FLASH SEDANG BERJALAN!\nMemutus paksa sekarang bisa membuat ECU BRICK (gagal flash). Lanjutkan hanya jika proses macet.\n\nPaksa DISCONNECT sekarang?"
      : (reading || connecting) ? "Proses sedang berjalan. Hentikan dan paksa DISCONNECT sekarang?" : "Putuskan koneksi ECU sekarang?";
    if (!(await askConfirm(msg))) return;
    manager.abort = true;
    try { await manager.disconnect(); } catch (_) {}
    setConnected(false); setConnecting(false); setFlashing(false); setReading(false);
    setDevice(null); setProgress(0); setSpeed(0);
    setColdGuide({ open: false, phase: "", seconds: 0 });
    log(busyWrite ? "WARN" : "INFO", busyWrite ? "FORCE DISCONNECT during WRITE — ECU may need recovery flash." : "Force disconnect — port closed.");
    if (busyWrite) toast.warning("Force disconnected", { description: "WRITE dihentikan — ECU mungkin perlu recovery." });
    else toast.info("Disconnected");
  }, [flashing, reading, connecting, manager, askConfirm, log]);

  const onConnectBtn = useCallback(() => {
    if (connected || connecting || flashing || reading) return forceDisconnect();
    return handleConnect();
  }, [connected, connecting, flashing, reading, forceDisconnect, handleConnect]);

  // Auto-disconnect + reset all displayed data to Flash Tools defaults (log is preserved).
  const resetToDefault = useCallback(async () => {
    try { await manager.disconnect(); } catch (_) {}
    setConnected(false);
    setDevice(null);
    setFirmware(null);
    setStartOffset("0");
    setChecksumOffset("0");
    setFlashSize("");
    setSmartChecksum(false);
    setFastWrite(true);
    setProgress(0);
    setSpeed(0);
    setProcStatus("");
    setEepromBytes(null);
    setEepromName("");
    setDtcRows([]);
    toast.info("Auto-disconnect", { description: "Perangkat diputus otomatis setelah proses. Log tetap disimpan." });
  }, [manager, log]);

  const handleFlash = useCallback(async () => {
    if (!firmware) { toast.error("Open a .bin file first"); return; }
    if (!connected) { toast.error("Connect the ECM first"); return; }
    if (device?.ecmId && device.ecmId !== "-" && !device.recovery && !/^01[0-9A-F]{8}$/i.test(device.ecmId)) {
      log("ERROR", `Invalid ECM ID ${device.ecmId} — WRITE blocked. Check voltage/cable, reconnect.`);
      toast.error("ECM ID tidak valid", { description: "Cek tegangan/kabel lalu connect ulang." });
      return;
    }
    setFlashing(true); setProgress(0); setSpeed(0);
    log("INFO", "=== FLASH SESSION START ===");
    let fw = firmware;
    const is96 = firmware.size === 98304;
    if (smartChecksum && (is96 || (device?.maker !== "Denso" && (firmware.size !== 393216 || device?.maker === "Shindengen")))) {
      try {
        const src = new Uint8Array(firmware.bytes);
        const cr = autoCorrectChecksum(src, parseInt(firmware.checksumOffset || "0", 16));
        if (cr.ok && cr.changed) {
          fw = { ...firmware, bytes: cr.out };
          log("SUCCESS", `FIX CHECKSUM: ${cr.type?.ecuMaker || "checksum"} @0x${cr.addr.toString(16).toUpperCase()} → 0x${(cr.corrected >>> 0).toString(16).toUpperCase()}`);
        } else if (cr.ok && cr.unknown) {
          log("WARN", "FIX CHECKSUM: unknown type — flashing as-is.");
        } else if (cr.ok) {
          log("INFO", "FIX CHECKSUM: checksum sudah valid — tidak ada perubahan.");
        } else {
          log("WARN", `FIX CHECKSUM: ${cr.error || "gagal"} — file di-flash apa adanya.`);
        }
      } catch (e) { log("WARN", `FIX CHECKSUM error: ${e.message} — file di-flash apa adanya.`); }
    } else if (firmware.size === 393216) {
      const mk = device?.maker === 'Shindengen' ? 'Shindengen' : (device?.maker === 'Denso' ? 'Denso' : '384KB');
      log("INFO", `${mk} 384KB: flashing verbatim (checksum unchanged).`);
    }
    // In RECOVERY the connected ECU has no readable ID — derive it from the loaded BIN so the
    // Denso key constants (keyed by ECM ID) still resolve and the re-flash can unlock.
    const _binInfo = (device?.recovery || !device?.ecmId || device.ecmId === "-") ? ecmIdFromBin(new Uint8Array(fw.bytes)) : null;
    const recoveryEcmId = (_binInfo && _binInfo.ecmId) || device?.ecmId;
    if (_binInfo && _binInfo.ecmId && _binInfo.ecmId !== device?.ecmId) log("INFO", `Recovery: using BIN ECM ID ${_binInfo.ecmId}.`);
    // Parse optional manual recovery key/constants from the user's backdoor (Denso recovery only).
    let recoveryConsts = null, recoveryKey = null;
    const _rkTok = (recoveryKeyInput || "").trim().split(/[\s,]+/).filter(Boolean);
    if (_rkTok.length >= 3) recoveryConsts = _rkTok.slice(0, 3).map((t) => parseInt(t.replace(/0x/i, ""), 16) & 0xffff);
    else if (_rkTok.length === 1) recoveryKey = _rkTok[0];
    const res = await runFlash(
      manager, fw,
      { startOffset, checksumOffset, fastWrite, verify: true, maker: device?.maker, mcu: device?.chip, part: device?.part, ecmId: recoveryEcmId, recovery: device?.recovery, recoveryConsts, recoveryKey },
      { onLog: log, onProgress: (_ph, p) => setProgress(p), onPhase: () => {}, onSpeed: setSpeed }
    );
    setFlashing(false); setSpeed(0);
    if (res.result !== "success") setProgress(0);
    setFlashResult({ ok: res.result === "success", result: res.result, file: firmware.name, seconds: res.duration_ms ? (res.duration_ms / 1000).toFixed(1) : null });
    saveFlashSession({
      device_id: device?.ecmId || "-", part_number: device?.part || "-", firmware_name: firmware.name,
      firmware_size: firmware.size, start_offset: startOffset, checksum_offset: checksumOffset,
      fast_write: fastWrite, result: res.result, duration_ms: res.duration_ms,
      log: logs.filter((l) => !isSensitiveLog(l)).slice(-60).map((l) => `[${l.time}] ${l.level} ${l.msg}`),
    });
    resetToDefault();
  }, [firmware, connected, manager, startOffset, checksumOffset, fastWrite, smartChecksum, device, log, logs, resetToDefault, recoveryKeyInput]);

  // Denso TEST UNLOCK — non-destructive: wake → ID → security → key → report, STOP before erase.
  const handleDensoUnlock = useCallback(async () => {
    if (!connected) { toast.error("Connect the ECM first"); return; }
    setFlashing(true);
    log("SECURITY", "=== DENSO TEST UNLOCK (AMAN: tanpa erase/write) ===");
    try {
      const res = await testUnlockDenso(
        manager,
        { maker: device?.maker, mcu: device?.chip, part: device?.part, ecmId: device?.ecmId },
        { onLog: log, onProgress: () => {}, onPhase: () => {}, onSpeed: () => {} }
      );
      if (res.result === "success") toast.success("Test Unlock OK", { description: `Seed ${res.seed} → Key ${res.key} diterima (status 0x00)` });
      else toast.error("Test Unlock gagal", { description: "Cek log untuk status/NRC." });
    } catch (e) { log("ERROR", `Test unlock error: ${e.message}`); toast.error("Test unlock error"); }
    setFlashing(false);
  }, [connected, manager, device, log]);

  const guardConn = () => { if (!connected) { toast.error("Connect the ECM first"); return false; } return true; };
  // Denso READ PROBE — read-only: unlock, then try unused bootloader commands; never erase/write.
  const handleDensoProbe = useCallback(async () => {
    if (!connected) { toast.error("Connect the ECM first"); return; }
    const ok = await askConfirm("PROBE READ DENSO #3: TRMD akan unlock, mengirim perintah RANGE (status 0x20), lalu mencoba perintah baca. Erase/write TIDAK dikirim. Risiko: ECU bisa perlu recovery. Matikan IGNITION 15 detik setelahnya. Lanjut?");
    if (!ok) return;
    setReading(true);
    log("SECURITY", "=== DENSO READ PROBE (tanpa erase/write) ===");
    const ref = firmware?.bytes && firmware.bytes.length === 393216 ? new Uint8Array(firmware.bytes) : null;
    log("INFO", ref ? `Reference BIN: ${firmware.name || "loaded"} (384KB)` : "Tidak ada BIN 384KB terbuka — hasil tidak bisa dicocokkan.");
    try {
      const res = await probeReadDenso(manager, { maker: device?.maker, mcu: device?.chip, part: device?.part, ecmId: device?.ecmId, refBytes: ref },
        { onLog: log, onProgress: () => {}, onPhase: () => {}, onSpeed: () => {} });
      if (res.hits && res.hits.length) toast.success("Probe menemukan kandidat perintah baca"); else toast.message("Probe selesai", { description: "Kirim log ke saya" });
    } catch (e) { log("ERROR", `Probe error: ${e.message}`); }
    setReading(false);
  }, [connected, manager, device, log, firmware, askConfirm]);
  const doReadDtc = useCallback(async () => {
    if (!guardConn()) return;
    setDtcBusy(true); log("INFO", "Reading DTC (self-diagnosis 0x74 current / 0x73 past)...");
    try {
      const r = await readDtc(manager);
      (r.raw || []).forEach((x) => log("UART", `DTC ${x}`));
      const map = new Map();
      (r.current || []).forEach((e) => { const m = map.get(e.code) || { code: e.code }; m.current = e.count; map.set(e.code, m); });
      (r.stored || []).forEach((e) => { const m = map.get(e.code) || { code: e.code }; m.past = e.count; map.set(e.code, m); });
      const rows = [...map.values()].sort((a, b) => a.code - b.code).map((m, i) => ({ no: i + 1, code: m.code, desc: dtcName(m.code), current: m.current, past: m.past }));
      setDtcRows(rows);
      if (rows.length) { log("WARN", `${rows.length} DTC(s) found`); showInfo(`${rows.length} DTC(s) found`); }
      else { log("SUCCESS", "No DTC — system OK"); showInfo("No DTC — system OK"); }
    } catch (e) { log("ERROR", `Read DTC failed: ${e.message}`); }
    setDtcBusy(false);
  }, [connected, manager, log]);
  const doClearDtc = useCallback(async () => {
    if (!guardConn()) return;
    if (!(await askConfirm("Hapus semua DTC tersimpan dari ECU?"))) return;
    setDtcBusy(true); log("WARN", "Clearing DTCs...");
    try { const r = await clearDtc(manager); log(r.ok ? "SUCCESS" : "WARN", r.ok ? `DTCs cleared. RX ${r.hex}` : `No ACK. RX ${r.hex || "(none)"}`); if (r.ok) { showInfo("DTCs cleared"); setDtcRows([]); } else toast.warning("Clear sent — no ACK (verify on ECU)"); } catch (e) { log("ERROR", `Clear DTC failed: ${e.message}`); }
    setDtcBusy(false);
  }, [connected, manager, log]);
  const doResetEcm = useCallback(async () => {
    if (!guardConn()) return;
    if (!(await askConfirm("Reset ECM? Siklus ignition OFF/ON setelahnya."))) return;
    setDtcBusy(true); log("WARN", "Resetting ECM...");
    try { const r = await resetEcm(manager); log(r.ok ? "SUCCESS" : "WARN", r.ok ? `ECM reset OK. RX ${r.hex}` : `Reset sent — no ACK. RX ${r.hex || "(none)"}`); toast[r.ok ? "success" : "warning"](r.ok ? "ECM reset" : "Reset sent — verify on ECU"); } catch (e) { log("ERROR", `Reset ECM failed: ${e.message}`); }
    setDtcBusy(false);
  }, [connected, manager, log]);

  const startPoll = useCallback(() => {
    if (!connected) { toast.error("Connect the ECM first"); return; }
    if (pollRef.current) return;
    setLivePoll(true); log("INFO", "Live data started.");
    pollRef.current = setInterval(async () => {
      try {
        const res = await readLiveData(manager, undefined, { withO2: true });
        const p = res?.parsed || (res?.data && parseLiveData(res.data));
        if (!p) return;
        const o2d = res?.o2?.diag;
        if (o2d) { if (o2d.o2v != null) p.o2 = o2d.o2v; if (o2d.stft != null) p.stft = o2d.stft; }
        setSample(p);
        setHist((h) => { const nh = { ...h }; SENSORS.forEach((s) => { const arr = (nh[s.key] || []).concat(Number.isFinite(p[s.key]) ? p[s.key] : 0); if (arr.length > 150) arr.shift(); nh[s.key] = arr; }); return nh; });
        setPeaks((pk) => { const np = { ...pk }; SENSORS.forEach((s) => { if (Number.isFinite(p[s.key])) np[s.key] = Math.max(np[s.key] ?? -Infinity, p[s.key]); }); return np; });
      } catch (_) {}
    }, 300);
  }, [connected, manager, log]);
  const stopPoll = useCallback(() => {
    const wasRunning = !!pollRef.current;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setLivePoll(false);
    if (wasRunning) log("INFO", "Live data stopped.");
  }, [log]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  // ENGINE DATA tab: auto-connect + start live polling on open; stop when leaving the tab.
  useEffect(() => {
    if (tab === "engine") {
      if (connected) startPoll();
      else if (!connecting) handleConnect();
    } else {
      stopPoll();
    }
    return () => { stopPoll(); };
  }, [tab, connected]); // eslint-disable-line react-hooks/exhaustive-deps


  // Refresh the COM ports the local bridge can see (CH340 / FTDI / CP210x — VAG KKL & others).
  const portBusyRef = useRef({});
  const portBusy = connected || connecting || flashing || reading || zxBusy || dtcBusy;
  portBusyRef.current = { busy: portBusy };
  const loadPorts = useCallback(async () => {
    if (portBusyRef.current.busy) return;  // never poll: fetchBridgePorts opens a 2nd WS that drops the live bridge session
    try { const r = await fetchBridgePorts(BRIDGE_URL); if (r.busy) return; setPorts(r.ports || []); }
    catch (_) { setPorts([]); }
  }, []);
  useEffect(() => {
    if (!portBusy) return;
    BRIDGE_LOCK.n += 1; markBridgeBusy();
    const hb = setInterval(markBridgeBusy, 2000);
    return () => { clearInterval(hb); BRIDGE_LOCK.n = Math.max(0, BRIDGE_LOCK.n - 1); if (!BRIDGE_LOCK.n) clearBridgeBusy(); };
  }, [portBusy]);
  // Auto-refresh the port list while NOT connected so the Device/Port always reflect whatever is
  // currently plugged in (swap cable / change adapter → list updates within ~2.5s, no manual Refresh).
  useEffect(() => {
    if (portBusy) return;
    loadPorts();
    const t = setInterval(loadPorts, 2500);
    return () => clearInterval(t);
  }, [loadPorts, portBusy]);
  // Tactrix Openport = J2534 (op20pt32.dll), no COM port → offer a virtual "openport" value.
  // Serial adapters (FTDI/CH340/CP210x) must pick a real COM port (no Auto).
  useEffect(() => {
    if (deviceType === "sm3") { setComPort((c) => (SM_NAMES[c] ? c : "sm3")); return; }
    if (deviceType === "tactrix") {
      const t = ports.find((p) => p.kind === "tactrix") || ports.find((p) => p.kind === "j2534");
      setComPort(t ? t.port : "openport");
      return;
    }
    const pool = ports.filter((p) => p.kind === deviceType || p.kind === "usb");
    const names = pool.map((p) => p.port);
    const byKind = pool[0];
    const fallback = null;
    setComPort((cur) => {
      if (cur && cur !== "openport" && names.includes(cur)) {
        const cp = pool.find((p) => p.port === cur);
        if (cp && (cp.kind === deviceType || cp.kind === "usb")) return cur;   // keep a valid same-kind manual pick
      }
      return (byKind && byKind.port) || (fallback && fallback.port) || "";
    });
  }, [deviceType, ports]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-detect the PLUGGED adapter and switch Device Type to match. Serial ports exist only when
  // plugged; the Openport J2534 is flagged conn=true only when the physical device is detected.
  const autoDevRef = useRef("");
  useEffect(() => {
    if (portBusy) return;
    const sig = ports.map((p) => `${p.kind}:${p.port}:${p.conn ? 1 : 0}`).join("|");
    if (sig === autoDevRef.current) return;      // react only to real plugged-hardware changes
    autoDevRef.current = sig;
    const op = ports.find((p) => (p.kind === "tactrix" || p.kind === "j2534") && p.conn);
    const sm = ports.find((p) => p.kind === "sm3" && p.conn);
    const ch = ports.find((p) => p.kind === "ch340");
    const ft = ports.find((p) => p.kind === "ftdi");
    const detected = sm ? "sm3" : op ? "tactrix" : ch ? "ch340" : ft ? "ftdi" : null;
    if (detected && detected !== deviceType) setDeviceType(detected);
    // SM3 USB / SM2 USB auto-picked from the plugged device; SM3 Wi-Fi is manual-only (never auto).
    const smUsb = ports.find((p) => p.sm && p.conn);
    if (smUsb) setComPort(smUsb.sm);
  }, [ports, portBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clamp EEPROM size to the valid options for the selected maker (Keihin 256/512, Shindengen 512/1024).
  useEffect(() => {
    const valid = toolMaker === "Keihin" ? [512] : [512, 1024];
    if (!valid.includes(eepromSize)) setEepromSize(512);
  }, [toolMaker]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- TOOLS: cold-read arming (IG OFF → release port → 10s → IG ON → fresh port) ----------
  const coldArm = useCallback(async (label, { probe = true, noTimer = false } = {}) => {
    if (!comPort) {
      log("ERROR", "No COM port selected — click Refresh and pick one.");
      toast.error("Pilih Port dulu", { description: "Tidak ada mode Auto — pilih COM port adapter secara manual." });
      throw new Error("no port selected");
    }
    const bridgeArgs = { url: BRIDGE_URL, baud: BAUD, device: isJ(deviceType) ? undefined : comPort, dll: deviceType === "sm3" ? comPort : deviceType };
    log("WARN", `🔻 ${label} — turn IGNITION OFF now (auto-detect)…`);
    setColdGuide({ open: true, phase: "ig_off", seconds: 0 });
    {
      // Auto-detect IGNITION OFF (CH340 + SM3/J2534): poll StartComm until the ECU stops answering 3x in a row.
      if (!connected) { await manager.connectBridge(bridgeArgs); setConnected(true); }
      let misses = 0, sawOn = false;
      const t0 = Date.now();
      while (misses < 3) {
        if (Date.now() - t0 > 90000) throw new Error("Ignition OFF not detected within 90s — aborted.");
        await manager.klineWake().catch(() => {});
        await manager.sendFramed([0xFE, 0x04, 0xFF, 0xFF], { timeout: 60, pre: 0 }).catch(() => {});
        const r = await manager.sendFramed([0x72, 0x05, 0x00, 0xF0, 0x99], { timeout: 350, pre: 0 }).catch(() => null);
        const alive = !!(r && r.reply && r.reply[0] === 0x02);
        if (alive) { if (!sawOn) log("INFO", "ECU still ON — waiting for IGNITION OFF…"); sawOn = true; misses = 0; } else misses++;
        await sleep(400);
      }
      log("SUCCESS", "IGNITION OFF detected (ECU silent).");
    }
    {
      log("INFO", "Auto-disconnecting bridge to release the port…");
      try { await manager.disconnect(); } catch (_) {}
      setConnected(false); setDevice(null);
      await sleep(1000);
    }
    // noTimer (J2534 Keihin READ, TunerForge-style): no 10 s countdown — open the adapter at once and let the engine poll; user turns IGNITION ON when the engine prompts.
    if (!(noTimer && isJ(deviceType))) {
    log("WARN", "⏳ KEEP IGNITION OFF — port released. Arming in 10s…");
    for (let sec = 10; sec > 0; sec--) {
      setColdGuide({ open: true, phase: "arming", seconds: sec });
      if (sec % 5 === 0) log("INFO", `…arming in ${sec}s (keep IGNITION OFF, port disconnected)`);
      await sleep(1000);
    }
    }
    if (isJ(deviceType)) {
      // Openport opens slowly (~7s) — open it while IGNITION is still OFF so the bridge catches the ECU boot.
      setColdGuide({ open: true, phase: "prep", seconds: 0 });
      log("INFO", `Opening ${deviceType === "sm3" ? (SM_NAMES[comPort] || "SM3 USB") : "Openport"} while IGNITION OFF…`);
      await manager.connectBridge(bridgeArgs);
      setConnected(true);
      log("SUCCESS", `${deviceType === "sm3" ? (SM_NAMES[comPort] || "SM3 USB") : "Openport"} ready.`);
      // Banner stays up until the engine logs "Security OK" (see log()) or the action ends.
      setColdGuide({ open: true, phase: "ig_on", seconds: 0 });
      log("WARN", "🔑🔑 NOW TURN IGNITION ON! 🔑🔑");
      toast.warning("NYALAKAN IGNITION (ON) SEKARANG!", { description: `${deviceType === "sm3" ? (SM_NAMES[comPort] || "SM3") : "Openport"} sudah siap.`, duration: 8000 });
      return;
    } else {
    setColdGuide({ open: true, phase: "ig_on", seconds: 0 });
    log("WARN", "🔑🔑 NOW TURN IGNITION ON! 🔑🔑");
    await sleep(1500);
    log("INFO", "Connecting fresh port at power-on…");
    await manager.connectBridge(bridgeArgs);
    setConnected(true);
    log("SUCCESS", "Native bridge — connected fresh.");
    }
    // Same power-on wake the old "Auto" port probe did (BREAK → FE 04 FF FF → StartComm, until 02 04 00 FA).
    // A fixed COM port skips that probe, so the ECU stayed silent for the arm/read frames.
    if (probe && !isJ(deviceType)) {
      let awake = false;
      for (let i = 0; i < 12 && !awake; i++) {
        await manager.klineWake().catch(() => {});
        await manager.sendFramed([0xFE, 0x04, 0xFF, 0xFF], { timeout: 60, pre: 0 }).catch(() => {});
        const r = await manager.sendFramed([0x72, 0x05, 0x00, 0xF0, 0x99], { timeout: 350, pre: 0 }).catch(() => null);
        awake = !!(r && r.reply && r.reply[0] === 0x02 && r.reply[2] === 0x00);
      }
      log(awake ? "SUCCESS" : "WARN", awake ? "ECU awake at power-on (StartComm 02 04 00 FA)." : "No StartComm reply at power-on — continuing anyway.");
      await sleep(60);
    }
    setColdGuide({ open: false, phase: "", seconds: 0 });
  }, [connected, manager, log, comPort, deviceType]);

  const saveBackup = useCallback(async (res, ident, opts = {}) => {
    log("INFO", `Total time: ${((res.duration_ms || 0) / 1000).toFixed(1)}s`);
    if (res.bytes && res.bytes.length) {
      const dev = ident || device;
      const dt = new Date(); const pad = (n) => String(n).padStart(2, "0");
      const dateStr = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
      const timeStr = `${pad(dt.getHours())}-${pad(dt.getMinutes())}-${pad(dt.getSeconds())}`;
      const partCode = (dev?.part || "").split(" · ")[0].replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
      const ecmStr = (dev?.ecmId && dev.ecmId !== "-" ? dev.ecmId : "").replace(/[^A-Za-z0-9._-]+/g, "-");
      const namePart = partCode || ecmStr || "ECU";
      const eeTag = opts.eeprom ? "EEPROM_" : "";
      const base = `${namePart}_${eeTag}${dateStr}_${timeStr}`;
      log(res.result === "success" ? "SUCCESS" : "WARN", `Read ${res.result} — ${res.bytes.length} B. Klik CHOOSE FOLDER & SAVE untuk beri nama & pilih folder.`);
      setSaveNameSuffix("");
      setSaveDlg({ bytes: res.bytes, base });
    } else {
      toast.error("Read failed — no data");
    }
  }, [device, log]);

  const doSaveConfirm = useCallback(async () => {
    if (!saveDlg) return;
    const finalName = `${saveDlg.base}${saveNameSuffix || ""}.bin`;
    await saveBinaryWithPicker(saveDlg.bytes, finalName, { onLog: log, toast });
    setSaveDlg(null);
  }, [saveDlg, saveNameSuffix, log]);

  const doReadFlash = useCallback(async () => {
    if (reading) return;
    const bytes = SIZE_BYTES[readSize] || 262144;
    const ident = device;
    setReading(true); setProgress(0); setSpeed(0);
    const cb = { onLog: log, onProgress: (_ph, p) => setProgress(p), onPhase: () => {}, onSpeed: setSpeed };
    try {
      if (toolMaker === "Shindengen") {
        log("INFO", "=== READ FLASH (Shindengen · cold) START ===");
        // Proven v1.9.82 flow: no StartComm probe at power-on — bridge cold wake must get the 0E ACK first.
        await coldArm("READ Shindengen", { probe: false });
        const res = await runRead(manager, { size: bytes, maker: "Shindengen", cold: true, firmwareBytes: firmware?.bytes, fast: true }, cb);
        await saveBackup(res, ident);
      } else {
        log("INFO", "=== READ FLASH (Keihin · cold) START ===");
        await coldArm("READ Keihin", { probe: bytes !== 262144 && bytes !== 65536, noTimer: deviceType === "sm3" && bytes === 262144 });
        let res;
        if (bytes === 65536) {
          res = await runRead(manager, { size: 65536, basePage: 0x00, maker: "Keihin", password: securityPw, keihinCold: true, keihinNative: true, turboBlk: 12, firmwareBytes: firmware?.bytes }, cb);
        } else if (bytes === 262144) {
          res = await runRead(manager, { size: 262144, maker: "Keihin", password: securityPw, keihinCold: true, keihinNative: true, turboBlk: 12, secDelay: deviceType === "sm3" ? 0 : undefined, k2f: /^010408/i.test(device?.ecmId || "") || /K2F/i.test(device?.part || ""), firmwareBytes: firmware?.bytes }, cb);
        } else {
          const map = { 393216: [0x60000, 0x0000, 0x00], 524288: [0x80000, 0x0000, 0x00], 1048576: [0x100000, 0x0000, 0x00], 131072: [0x20000, 0x0000, 0x0E], 98304: [0x18000, 0x0000, 0x0E], 65536: [0x10000, 0x0000, 0x00], 57344: [0xE000, 0x0000, 0x00], 49152: [0xC000, 0x4000, 0x00] };
          const [sz, startOff, basePage] = map[bytes] || [0x10000, 0x0000, 0x00];
          const arm = await armKeihinR8CRead(manager, { password: securityPw }, { onLog: log });
          if (!arm.armed) { log("ERROR", "Enter-read failed — aborting."); toast.error("Enter-read failed", { description: "ECU did not confirm read-mode." }); return; }
          res = await runRead(manager, { size: sz, startOffset: startOff, basePage, maker: "Keihin", r8c48: true, password: securityPw }, cb);
        }
        await saveBackup(res, ident);
      }
    } catch (e) { log("ERROR", `Read failed: ${e.message}`); toast.error("Read failed", { description: e.message }); }
    finally { setReading(false); setSpeed(0); setColdGuide({ open: false, phase: "", seconds: 0 }); resetToDefault(); }
  }, [reading, readSize, toolMaker, device, firmware, securityPw, manager, log, saveBackup, coldArm, resetToDefault]);

  // Denso READ via application mode (Sahara style): no bootloader, no erase/write.
  const handleDensoAppProbe = useCallback(async () => {
    if (!connected) { toast.error("Connect the ECM first"); return; }
    setReading(true);
    log("SECURITY", "=== DENSO PROBE READ MODE APLIKASI (tanpa bootloader/erase/write) ===");
    const ref = firmware?.bytes && firmware.bytes.length === 393216 ? new Uint8Array(firmware.bytes) : null;
    log("INFO", ref ? `Reference BIN: ${firmware.name || "loaded"} (384KB)` : "Tidak ada BIN 384KB terbuka — hasil tidak bisa dicocokkan.");
    try {
      const res = await probeReadDensoApp(manager, { refBytes: ref }, { onLog: log });
      setDensoMethod(res.method);
      if (res.method) toast.success("Perintah baca ditemukan", { description: "Tombol Read 384KB aktif" }); else toast.message("Probe selesai", { description: "Kirim log ke saya" });
    } catch (e) { log("ERROR", `Probe error: ${e.message}`); }
    setReading(false);
  }, [connected, manager, log, firmware]);

  const handleDensoAppRead = useCallback(async () => {
    if (!connected || !densoMethod) return;
    setReading(true); setProgress(0); setSpeed(0);
    log("INFO", "=== DENSO READ 384KB (mode aplikasi) START ===");
    try {
      const res = await readDensoApp(manager, { method: densoMethod }, { onLog: log, onProgress: (_ph, p) => setProgress(p), onPhase: () => {}, onSpeed: setSpeed });
      if (res.bytes) {
        const h = (v) => (v >>> 0).toString(16).toUpperCase().padStart(8, "0");
        const c = h(crc32(res.bytes));
        const refC = firmware?.bytes && firmware.bytes.length === 393216 ? h(crc32(new Uint8Array(firmware.bytes))) : null;
        log(refC && c === refC ? "SUCCESS" : "WARN", `CRC32 read ${c}${refC ? ` · referensi ${refC} → ${c === refC ? "IDENTIK" : "BERBEDA"}` : ""}`);
        await saveBackup(res, device);
      } else toast.error(`Read ${res.result}`);
    } catch (e) { log("ERROR", `Read failed: ${e.message}`); }
    finally { setReading(false); setSpeed(0); }
  }, [connected, densoMethod, manager, log, firmware, device, saveBackup]);

  const doReadEeprom = useCallback(async () => {
    if (reading) return;
    const ident = device;
    setReading(true); setProgress(0);
    try {
      if (toolMaker === "Shindengen") {
        log("INFO", `=== SHINDENGEN EEPROM READ (cold · ${eepromSize} B) START ===`);
        await coldArm("EEPROM Read Shindengen");
        const res = await runReadEepromShindengen(manager, { onLog: log, onProgress: (_p, p) => setProgress(p), onPhase: () => {} }, eepromSize);
        if (res.result === "success") { if (res.bytes) { setEepromBytes(res.bytes); setEepromName("read.bin"); } await saveBackup(res, ident, { eeprom: true }); } else toast.error(`EEPROM read ${res.result}`);
      } else {
        log("INFO", "=== EEPROM COLD READ (Keihin) START ===");
        await coldArm("EEPROM Read Keihin", /^010408/i.test(device?.ecmId || "") ? { probe: false } : undefined);
        const res = await runReadEepromKeihin(manager, { password: securityPw }, { onLog: log, onProgress: (_ph, p) => setProgress(p), onPhase: () => {}, onSpeed: setSpeed });
        if (res.result === "success" || res.result === "partial") { if (res.bytes) { setEepromBytes(res.bytes); setEepromName("read.bin"); } await saveBackup(res, ident, { eeprom: true }); } else toast.error(`K93 EEPROM read ${res.result}`);
      }
    } catch (e) { log("ERROR", `EEPROM read failed: ${e.message}`); toast.error("EEPROM read failed", { description: e.message }); }
    finally { setReading(false); setColdGuide({ open: false, phase: "", seconds: 0 }); resetToDefault(); }
  }, [reading, toolMaker, device, securityPw, manager, log, saveBackup, coldArm, eepromSize, resetToDefault]);

  const doFlashCount = useCallback(async () => {
    if (reading) return;
    if (!(await askConfirm("Clear / reset flash count pada ECU? Pastikan ECU siap dan aman."))) return;
    setReading(true); setProgress(0);
    const t0 = Date.now();
    const done = (ok, result) => setFlashResult({ kind: "count", ok, result, file: `Clear Flash Count · ${toolMaker}`, seconds: ((Date.now() - t0) / 1000).toFixed(1) });
    try {
      if (toolMaker === "Shindengen") {
        log("INFO", "=== SHINDENGEN CLEAR FLASH COUNT (cold) START ===");
        await coldArm("Clear Count Shindengen");
        const res = await runClearCountShindengen(manager, { onLog: log });
        done(!!res.ok, res.ok ? null : "no ACK");
      } else {
        log("INFO", "=== KEIHIN CLEAR FLASH COUNT (cold) START ===");
        await coldArm("Clear Count Keihin", /^010408/i.test(device?.ecmId || "") ? { probe: false } : undefined);
        const res = await runClearCountKeihin(manager, { k2f: /^(010408|010232)/i.test(device?.ecmId || "") }, { onLog: log, onProgress: (_ph, p) => setProgress(p), onPhase: () => {} });
        done(!!res.ok, res.ok ? null : "no ACK");
      }
    } catch (e) { log("ERROR", `Clear count failed: ${e.message}`); done(false, e.message); }
    finally { setReading(false); setColdGuide({ open: false, phase: "", seconds: 0 }); }
  }, [reading, toolMaker, device, manager, log, coldArm]);

  const handleOpenEeprom = useCallback(async (file) => {
    if (!file) return;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (![256, 512, 1024].includes(bytes.length)) { toast.error("EEPROM .bin harus 256/512/1024 byte", { description: `Got ${bytes.length}B` }); return; }
      setEepromBytes(bytes); setEepromName(file.name);
      log("INFO", `EEPROM source: ${file.name} (${bytes.length} B)`);
      toast.success("EEPROM file loaded", { description: `${file.name} · ${bytes.length} B` });
    } catch (e) { toast.error("Gagal baca file", { description: e.message }); }
  }, [log]);

  const doWriteEeprom = useCallback(async () => {
    if (reading) return;
    if (!eepromBytes) { toast.error("Buka EEPROM .bin dulu (atau EEPROM READ)"); return; }
    const len = eepromBytes.length;
    if (toolMaker === "Keihin" && len !== 256 && len !== 512) { toast.error("Keihin EEPROM harus 256B/512B"); return; }
    if (toolMaker === "Shindengen" && len !== 512 && len !== 1024) { toast.error("Shindengen EEPROM harus 512B/1024B"); return; }
    if (!(await askConfirm(`WRITE EEPROM (${toolMaker}, ${len}B) ke ECU? Data salah bisa mengunci ECU. Lanjut?`))) return;
    setReading(true); setProgress(0);
    const t0 = Date.now();
    const done = (ok, result) => setFlashResult({ kind: "eeprom", ok, result, file: `${eepromName || "EEPROM"} · ${toolMaker} · ${len}B`, seconds: ((Date.now() - t0) / 1000).toFixed(1) });
    try {
      if (toolMaker === "Keihin") {
        const isK2F = /^010408/i.test(device?.ecmId || "");
        await coldArm("EEPROM Write Keihin", isK2F ? { probe: false } : undefined);
        const r = await runWriteEepromKeihin(manager, eepromBytes, { password: securityPw, k2f: isK2F || /^(010232|01027F)/i.test(device?.ecmId || "") || /K93/i.test(device?.part || "") }, { onLog: log, onProgress: (_p, p) => setProgress(p), onPhase: () => {} });
        done(r.result === "success", r.result);
      } else {
        await coldArm("EEPROM Write Shindengen");
        const r = await runWriteEepromShindengen(manager, eepromBytes, { onLog: log, onProgress: (_p, p) => setProgress(p), onPhase: () => {} });
        done(r.result === "success", r.result);
      }
    } catch (e) { log("ERROR", `EEPROM write: ${e.message}`); done(false, e.message); }
    finally { setReading(false); setColdGuide({ open: false, phase: "", seconds: 0 }); resetToDefault(); }
  }, [reading, eepromBytes, eepromName, toolMaker, securityPw, device, manager, log, coldArm, resetToDefault]);

  const doResetEeprom = useCallback(async (fill = "ff") => {
    if (reading) return;
    if (!(await askConfirm(`FORMAT EEPROM (${toolMaker}) → semua ke 0x${fill.toUpperCase()}? ECU immo akan di-blank. Lanjut?`))) return;
    setReading(true); setProgress(0);
    try {
      if (toolMaker === "Keihin") {
        await coldArm("EEPROM Reset Keihin");
        const r = await runFormatEepromKeihin(manager, { password: securityPw, fill }, { onLog: log, onProgress: (_p, p) => setProgress(p), onPhase: () => {}, onSpeed: () => {} });
        if (r.bytes) { setEepromBytes(r.bytes); setEepromName("format.bin"); }
        (r.result === "success") ? toast.success("Keihin EEPROM formatted → 0xFF") : toast.error(`Format ${r.result}`);
      } else {
        await coldArm("EEPROM Reset Shindengen");
        const blank = new Uint8Array(512).fill(fill === "00" ? 0x00 : 0xff);
        const r = await runWriteEepromShindengen(manager, blank, { onLog: log, onProgress: (_p, p) => setProgress(p), onPhase: () => {} });
        if (r.result === "success") { setEepromBytes(blank); setEepromName("blank.bin"); toast.success("Shindengen EEPROM di-blank → 0xFF"); } else toast.error(`EEPROM reset ${r.result}`);
      }
    } catch (e) { log("ERROR", `EEPROM reset: ${e.message}`); toast.error("EEPROM reset failed", { description: e.message }); }
    finally { setReading(false); setColdGuide({ open: false, phase: "", seconds: 0 }); }
  }, [reading, toolMaker, securityPw, manager, log, coldArm]);

  const csStatus = useMemo(() => {
    if (!firmware?.bytes) return null;
    try {
      let bytes = firmware.bytes;
      const fwCs = parseInt(firmware.checksumOffset || "0", 16) || 0;
      if (smartChecksum) {
        const cr = autoCorrectChecksum(new Uint8Array(firmware.bytes), fwCs);
        if (cr.ok && cr.out) bytes = cr.out;
      }
      const st = getChecksumStatus(bytes, fwCs);
      // 8-bit sum has no size default for 256KB (addr 0) → show the DB/auto slot (K2F 9FEF) instead.
      const addr = (st.addr === 0 && fwCs > 0) ? fwCs : st.addr;
      const list = listChecksums(bytes, fwCs).map((c) => (c.addr === 0 && fwCs > 0 ? { ...c, addr: fwCs } : c));
      const invalid = list.filter((c) => !c.valid);
      const state = list.length ? (invalid.length ? "invalid" : "valid") : (st.state === "valid" ? "valid" : "invalid");
      return { state, addr, list, invalid };
    } catch { return null; }
  }, [firmware, smartChecksum]);

  // Shindengen 384KB has a dynamic whole-file LE32 slot (checksum.js) → fixable; Denso 384KB is not.
  const csCanFix = !!firmware && (firmware.size === 98304 || (device?.maker !== "Denso" && (firmware.size !== 393216 || device?.maker === "Shindengen")));
  useEffect(() => {
    if (firmware && csStatus?.state === "invalid" && !smartChecksum) setCsWarn("load");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmware]);
  const onWriteClick = useCallback(() => {
    if (csStatus?.state === "invalid") { setCsWarn("write"); return; }
    handleFlash();
  }, [csStatus, handleFlash]);
  // OK — FIX CHECKSUM from the WRITE dialog: once the fixed status is valid, continue the write automatically.
  useEffect(() => {
    if (pendingWrite && smartChecksum && csStatus?.state === "valid") { setPendingWrite(false); handleFlash(); }
    else if (pendingWrite && smartChecksum && csStatus && csStatus.state !== "valid") { setPendingWrite(false); log("WARN", "FIX CHECKSUM tidak bisa memvalidasi file — WRITE dibatalkan."); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingWrite, smartChecksum, csStatus]);

  useEffect(() => {
    if (!firmware) return;
    if (smartChecksum && csStatus?.addr != null) setChecksumOffset(csStatus.addr.toString(16).toUpperCase());
    else setChecksumOffset(firmware.checksumOffset || "0");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartChecksum, csStatus, firmware]);

  useEffect(() => {
    if (device?.flash) {
      const lbl = dbFlashToLabel(device.flash);
      if (lbl) setFlashSize(lbl);
    }
  }, [device]);

  const d = device || {};
  const visibleLogs = canSeeFullLog ? logs : logs.filter((l) => !isSensitiveLog(l));
  const logText = visibleLogs.length ? visibleLogs.map((l) => `[${l.time}] ${fmtLogMsg(l)}`).join("\n") : "Click CONNECT ECM to start";
  const logLines = visibleLogs.map((l) => ({ level: l.level, text: `[${l.time}] ${fmtLogMsg(l)}` }));
  const isRemap = tab === "shindengen";
  const handleClose = () => {
    if (flashing || reading) { toast.error("Proses WRITE sedang berjalan — tidak bisa keluar", { description: "Tunggu sampai selesai." }); return; }
    if (embedded) onClose?.(); else navigate(window.tiroDesktopApp ? "/workspace" : "/");
  };


  // Kawasaki ZX-25R flash WRITE over CAN (KWP2000/ISO15765 0x764/0x746, Tactrix Openport).
  // Independent of the K-Line CONNECT — the bridge opens its own J2534 CAN channel.
  const handleKawasakiZxConnect = useCallback(async () => {
    setZxBusy(true);
    try {
      log("INFO", `Menghubungkan bridge ${BRIDGE_URL} (WS)…`);
      await manager.connectBridgeWsOnly({ url: BRIDGE_URL });
      log("SUCCESS", "Native bridge online.");
    } catch (e) {
      log("ERROR", `Bridge offline — start TRMDEngine.exe (${e.message})`);
      setZxBusy(false); return;
    }
    const res = await runKawasakiZx25rInfo(manager, { onLog: (lvl, m) => log(lvl, m) });
    if (res.result === "success") {
      setConnected(true);
      setToolMaker("Kawasaki");
      setDevice((d) => ({
        ...(d || {}),
        part: res.hw || "",      // HW ID → Code Part / Part Number
        ecmId: res.sw || "",     // SW ID → ECM ID
        maker: "Kawasaki",
        model: res.vin && res.vin !== "NO VIN RECORDED" ? res.vin : (d?.model || ""),
        connection: "Tactrix CAN",
      }));
      toast.success("ZX-25R connected", { description: `HW ${res.hw || "-"} · SW ${res.sw || "-"}` });
    } else {
      toast.error("Connect ZX-25R gagal", { description: "Cek Tactrix tersambung ke CAN/OBD & kunci kontak ON." });
    }
    setZxBusy(false);
  }, [manager, log]);

  const handleKawasakiZxUnlock = useCallback(async () => {
    setZxBusy(true);
    try {
      log("INFO", `Menghubungkan bridge ${BRIDGE_URL} (WS)…`);
      await manager.connectBridgeWsOnly({ url: BRIDGE_URL });
      log("SUCCESS", "Native bridge online.");
    } catch (e) {
      log("ERROR", `Bridge offline — start TRMDEngine.exe (${e.message})`);
      setZxBusy(false); return;
    }
    const res = await runKawasakiZx25rUnlock(manager, { onLog: (lvl, m) => log(lvl, m) });
    if (res.result === "success") toast.success("Unlock diterima ECU ✓", { description: `seed ${res.seed} → key ${res.key}` });
    else toast.error("Unlock ditolak / gagal", { description: res.nrc ? `nrc 0x${res.nrc}` : undefined });
    setZxBusy(false);
  }, [manager, log]);

  const handleKawasakiZx = useCallback(async () => {
    if (!firmware || !firmware.bytes) { toast.error("Muat file BIN ZX-25R (2 MB) dulu"); return; }
    if (firmware.bytes.length !== 0x200000) { toast.error(`BIN harus 2 MB (0x200000) — file ini ${firmware.bytes.length} B`); return; }
    if (!(await askConfirm("WRITE Kawasaki ZX-25R lewat CAN (Tactrix Openport).\n\nIni menulis flash ECU secara NYATA & destruktif. Pastikan kunci kontak ON dan Tactrix tersambung ke CAN/OBD.\n\nLanjutkan?"))) return;
    setZxBusy(true); setProgress(0);
    try {
      log("INFO", `Menghubungkan bridge ${BRIDGE_URL} (WS)…`);
      await manager.connectBridgeWsOnly({ url: BRIDGE_URL });
      log("SUCCESS", "Native bridge online.");
    } catch (e) {
      log("ERROR", `Bridge offline — start TRMDEngine.exe (${e.message})`);
      setZxBusy(false); return;
    }
    let lastPct = -10;
    await runKawasakiZx25rWrite(manager, { bytes: firmware.bytes }, {
      onLog: (lvl, m) => log(lvl, m),
      onProgress: (_ph, pct) => { setProgress(pct); if (pct - lastPct >= 10) { lastPct = pct; log("INFO", `Transfer ${pct}%`); } },
      onPhase: () => {},
    });
    setZxBusy(false);
  }, [firmware, manager, log]);

  return (
    <CompactCtx.Provider value={compact}>
    <div className={`${embedded ? "h-full [.ft-narrow_&]:h-auto" : (compact ? "h-[100dvh] overflow-hidden [.ft-narrow_&]:h-auto [.ft-narrow_&]:min-h-[100dvh] [.ft-narrow_&]:overflow-y-auto" : "min-h-[100dvh]")} flex flex-col bg-[#07090e] text-slate-200`} data-testid="honda-flashtools-page" data-i18n-lock="en">
      {confirmState && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[min(92vw,480px)] animate-in fade-in zoom-in-95 duration-200" data-testid="float-confirm" data-i18n-free>
          <div className="rounded-xl border border-amber-500/40 bg-[#0c1017]/95 backdrop-blur-md shadow-2xl shadow-black/60 p-5">
            <div className="flex items-start gap-3">
              {confirmState.info ? <CheckCircle2 className="text-emerald-400 shrink-0 mt-0.5" size={24} /> : <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={24} />}
              <div className="text-[15px] leading-relaxed text-slate-100 whitespace-pre-line flex-1">{confirmState.message}</div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              {!confirmState.info && <button data-testid="float-confirm-cancel" onClick={() => resolveConfirm(false)} className="px-5 py-2 rounded-lg text-[14px] font-semibold text-slate-200 bg-slate-700/60 hover:bg-slate-700 transition-colors">Batal</button>}
              <button data-testid="float-confirm-ok" onClick={() => resolveConfirm(true)} className="px-5 py-2 rounded-lg text-[14px] font-bold text-white bg-amber-600 hover:bg-amber-500 transition-colors">OK</button>
            </div>
          </div>
        </div>
      )}
      <TrmdBanner />
      <FlashResultDialog result={flashResult} onClose={() => setFlashResult(null)} />
      <ChecksumWarnDialog open={!!csWarn} addr={csStatus?.addr} invalid={csStatus?.invalid} canFix={csCanFix && !smartChecksum}
        onFix={() => { const w = csWarn === "write"; setSmartChecksum(true); setCsWarn(null); log("INFO", "FIX CHECKSUM diaktifkan."); if (w) setPendingWrite(true); }}
        onWriteAnyway={csWarn === "write" ? () => { setCsWarn(null); log("WARN", "WRITE dengan checksum INVALID (dikonfirmasi user)."); handleFlash(); } : null}
        onClose={() => setCsWarn(null)} />
      <div className={`w-full flex-1 min-h-0 flex flex-col ${compact ? "p-2 gap-2" : "p-3 gap-3"}`}>
        <div className={`flex-1 min-h-0 flex flex-col ${compact ? "gap-2" : "gap-3"}`}>
        {isRemap && (
        <>
        {/* INFO BIKE + INFO FILE */}
        <div className={`grid grid-cols-3 [.ft-narrow_&]:grid-cols-1 ${compact ? "gap-2" : "gap-4"} shrink-0`}>
          <div className="col-span-2 [.ft-narrow_&]:col-span-1 rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_8px_24px_rgba(0,0,0,0.5)]">
            <PanelHead icon={<img src="/trmd-moto.png" alt="" className="h-7 w-auto" />} title="INFO BIKE" />
            <div className={`grid grid-cols-2 max-[520px]:grid-cols-1 ${compact ? "gap-x-5 gap-y-2 p-3" : "gap-x-6 gap-y-3 p-4"}`}>
              <Row label="Model" value={d.model || ""} testid="info-model-top" />
              <Row label="Smart Key" value={d.smartKey || ""} testid="info-smartkey" />
              <Row label="Region" value={d.region || ""} testid="info-region" />
              <Row label="ECM Type" value={d.maker || ""} testid="info-ecmtype" />
              <Row label="Displacement" value={d.displacement || ""} testid="info-displacement" />
              <Row label="ECM ID" value={d.ecmId && d.ecmId !== "-" ? d.ecmId : ""} testid="info-ecmid" />
              <Row label="Year" value={d.year || ""} testid="info-year" />
              <Row label="Part Number" value={(d.origPart || d.part || "").split(" · ")[0]} redLabel testid="info-partnumber" />
            </div>
          </div>
          <div className="rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_8px_24px_rgba(0,0,0,0.5)]">
            <PanelHead icon={<FileText size={25} />} title="INFO FILE" right={
              <button data-testid="honda-close-btn" onClick={handleClose} title={flashing || reading ? "Terkunci saat WRITE" : "Tutup"} disabled={flashing || reading}
                style={{ clipPath: "polygon(24px 0, 100% 0, 100% 100%, 0 100%)" }}
                className={`h-full ${compact ? "pl-8 pr-4" : "pl-10 pr-5"} text-white flex items-center justify-center transition-colors ${flashing || reading ? "bg-black/30 opacity-40 cursor-not-allowed" : "bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f]"}`}>
                <X size={compact ? 18 : 22} strokeWidth={2.75} />
              </button>
            } />
            <div className={`flex flex-col ${compact ? "gap-2.5 p-3" : "gap-3 p-4"}`}>
              <div className="flex items-center gap-3">
                <span className={`w-36 shrink-0 ${compact ? "text-[14px]" : "text-[16px]"} font-medium text-white`}>Flash Size</span>
                <select data-testid="info-flashsize" value={flashSize} onChange={(e) => setFlashSize(e.target.value)}
                  className={`flex-1 ${compact ? "h-7" : "h-8"} px-2 rounded-[3px] border border-[#6b7280] bg-[#16181d] text-slate-100 text-[14px] font-mono outline-none focus:border-red-500`}>
                  <option value="">—</option>
                  {ALL_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <Row label="Start Offset" value={startOffset} readOnly={false} onChange={(v) => setStartOffset(v.replace(/[^0-9a-fA-F]/g, "").toUpperCase())} testid="info-startoffset" />
              <Row label="Checksum Offset" value={checksumOffset} readOnly={false} onChange={(v) => setChecksumOffset(v.replace(/[^0-9a-fA-Futo]/gi, "").toUpperCase())} testid="info-checksumoffset" />
              <div className={`flex items-center mt-1 flex-wrap gap-y-1 ${compact ? "gap-x-2.5" : "gap-x-6 gap-y-2"}`}>
                <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="info-fixchecksum">
                  <input type="checkbox" checked={smartChecksum} onChange={(e) => setSmartChecksum(e.target.checked)} className="h-4 w-4 accent-cyan-500 shrink-0" />
                  <span className={`whitespace-nowrap ${compact ? "text-[12px]" : "text-[15px]"} font-semibold text-slate-300`}>FIX CHECKSUM</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="info-fastwrite">
                  <input type="checkbox" checked={fastWrite} onChange={(e) => setFastWrite(e.target.checked)} className="h-4 w-4 accent-cyan-500 shrink-0" />
                  <span className={`whitespace-nowrap ${compact ? "text-[12px]" : "text-[15px]"} font-semibold text-slate-300`}>FAST WRITE</span>
                </label>
                <span className="inline-flex items-center gap-1.5 ml-1 whitespace-nowrap" data-testid="info-flashcount">
                  <span className={`${compact ? "text-[12px]" : "text-[13px]"} font-semibold text-slate-200`}>Flash Count:</span>
                  <span className={`${compact ? "text-[12px]" : "text-[14px]"} font-bold font-mono text-cyan-300`}>{d.flashCount && d.flashCount !== "-" ? d.flashCount : "—"}</span>
                </span>
              </div>
              <div className="flex items-center gap-3" data-testid="info-checksum-status">
                <span className={`w-36 shrink-0 ${compact ? "text-[13px]" : "text-[16px]"} font-medium text-white`}>Checksum Status</span>
                {csStatus ? (
                  <span className={`inline-flex items-center gap-2 text-[13px] font-bold ${csStatus.state === "valid" ? "text-emerald-300" : "text-amber-300"}`} data-testid="info-checksum-badge">
                    {csStatus.state === "valid" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                    {csStatus.state === "valid" ? "VALID" : "INVALID"}{csStatus.state !== "valid" && csStatus.invalid?.length
                      ? ` @${[...new Set(csStatus.invalid.map((c) => "0x" + c.addr.toString(16).toUpperCase()))].join(", @")}`
                      : (csStatus.addr != null ? ` @0x${csStatus.addr.toString(16).toUpperCase()}` : "")}
                    {csStatus.state !== "valid" && csStatus.invalid?.length > 1 && <span className="font-normal text-amber-200/80" data-testid="info-checksum-count">({csStatus.invalid.length} checksum)</span>}
                  </span>
                ) : (
                  <span className="text-slate-400 text-[13px]">—</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TASK */}
        <div className="rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_8px_24px_rgba(0,0,0,0.5)] flex-1 min-h-0 flex flex-col">
          <PanelHead icon={<ClipboardList size={25} />} title="TASK" />
          <div className={`flex flex-col flex-1 min-h-0 ${compact ? "p-2 gap-1.5" : "p-4 gap-3"}`}>
            <input ref={fileRef} type="file" accept=".bin,.hex,.s19,.mot" className="hidden" data-testid="honda-file-input"
              onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleFile(f); if (e.target) e.target.value = ""; }} />
            <div className={`grid grid-cols-3 [.ft-narrow_&]:grid-cols-1 flex-1 min-h-0 [.ft-narrow_&]:min-h-[380px] ${compact ? "gap-2" : "gap-4"}`}>
              <div className={`col-span-2 [.ft-narrow_&]:col-span-1 flex flex-col min-h-0 ${compact ? "gap-1.5" : "gap-2"}`}>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid="honda-open-bin" onClick={() => fileRef.current?.click()} disabled={flashing} title="OPEN .BIN"
                    className={`flex-1 min-w-0 ${compact ? "h-8" : "h-9"} rounded-[3px] border border-[#6b7280] bg-[#16181d] hover:border-red-500/60 px-4 flex items-center text-left text-[14px] font-mono text-slate-100 truncate transition-colors disabled:cursor-not-allowed`}>
                    <span data-testid="honda-file-name" className="truncate">{firmware ? `${firmware.name}  :  ${firmware.size} bytes` : "No file loaded — click to OPEN .BIN"}</span>
                  </button>
                  <button data-testid="honda-open-bin-btn" onClick={() => fileRef.current?.click()} disabled={flashing} title="OPEN .BIN"
                    className={`${compact ? "h-8 px-3 text-[13px]" : "h-9 px-4 text-[14px]"} shrink-0 rounded-[3px] bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold uppercase tracking-wide inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed`}>
                    <Upload size={16} /> Open File
                  </button>
                  <button data-testid="honda-copy-log" onClick={copyLog} title="Copy log" className={`${compact ? "h-8 w-8" : "h-9 w-9"} shrink-0 rounded-[3px] border border-[#6b7280] bg-[#161a22] hover:bg-[#222834] text-white inline-flex items-center justify-center`}><Copy size={18} /></button>
                  <button data-testid="honda-clear-log" onClick={clearLog} title="Hapus isi log" className={`${compact ? "h-8 w-8" : "h-9 w-9"} shrink-0 rounded-[3px] bg-red-700 hover:bg-red-600 text-white inline-flex items-center justify-center`}><Trash2 size={18} /></button>
                </div>
                <div className={`relative flex-1 min-h-0 ${compact ? "min-h-[40px] [.ft-narrow_&]:min-h-[150px]" : "min-h-[120px]"}`}>
                  <LogView boxRef={logBoxRef} lines={logLines} empty={logText} testid="honda-console" className="rounded-sm !pr-3 !bg-[#111317] !border-[#6b7280]" />
                </div>
              </div>
              <div className={`flex flex-col h-full min-h-0 pr-0.5 ${compact ? "gap-2" : "gap-3"}`}>
                <div className="flex items-center flex-nowrap gap-2 w-full overflow-hidden shrink-0" data-testid="honda-comport-row">
                  {renderDeviceControls("flex-1 min-w-0")}
                </div>
                <button data-testid="honda-connect-ecm" onClick={onConnectBtn}
                  className={`rounded border font-bold inline-flex items-center justify-center transition ${compact ? "flex-1 min-h-[40px] max-h-[110px] text-[15px] gap-2" : "flex-1 min-h-[48px] max-h-[150px] text-[16px] sm:text-[18px] gap-2 sm:gap-3"} ${(connected || connecting || flashing || reading) ? "border-2 border-red-600 bg-[#0b0c10] hover:bg-red-600/10 text-red-500 shadow-[inset_0_0_18px_rgba(220,38,38,0.25),0_0_10px_rgba(220,38,38,0.25)]" : "border-red-600 bg-red-600 hover:bg-red-700 text-white"}`}>
                  {(connected || connecting || flashing || reading) ? <Power size={compact ? 22 : 26} /> : <Link2 size={compact ? 22 : 26} />}
                  {(flashing || reading || connected) ? "DISCONNECT" : connecting ? "CONNECTING… · STOP" : "CONNECT ECM"}
                </button>
                <button data-testid="honda-flash" onClick={onWriteClick} disabled={!connected || !firmware || flashing}
                  className={`relative overflow-hidden rounded-sm border-0 bg-gradient-to-b from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed ${compact ? "flex-1 min-h-[40px] max-h-[110px] text-[15px] gap-2" : "flex-1 min-h-[48px] max-h-[150px] text-[16px] sm:text-[18px] gap-2 sm:gap-3"}`}>
                  <span aria-hidden className="absolute right-0 bottom-0 h-1/2 w-16 bg-black/25" style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }} />
                  <Download size={compact ? 22 : 26} /> {flashing ? "WRITING" : "WRITE"}
                </button>
                {/* ZX-25R (Kawasaki/MH8610F) hidden for now — seed→key still being reversed */}
                {false && (<>
                <button data-testid="honda-zx25r-connect" onClick={handleKawasakiZxConnect} disabled={flashing || zxBusy}
                  title="Baca HW/SW/VIN Kawasaki ZX-25R via CAN (Tactrix Openport) — tanpa K-Line"
                  className={`rounded border border-lime-500/40 bg-lime-500/10 hover:bg-lime-500/20 text-lime-300 font-bold inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed ${compact ? "flex-1 min-h-[56px] text-[14px] gap-2" : "flex-1 min-h-[64px] text-[15px] sm:text-[17px] gap-2 sm:gap-3"}`}>
                  <Link2 size={compact ? 20 : 24} /> {zxBusy ? "ZX-25R…" : "CONNECT ZX-25R (CAN)"}
                </button>
                <button data-testid="honda-zx25r-unlock" onClick={handleKawasakiZxUnlock} disabled={flashing || zxBusy}
                  title="Uji security access ZX-25R (AMAN: tanpa erase/write) — pastikan rumus key benar sebelum WRITE"
                  className={`rounded border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-bold inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed ${compact ? "flex-1 min-h-[56px] text-[14px] gap-2" : "flex-1 min-h-[64px] text-[15px] sm:text-[17px] gap-2 sm:gap-3"}`}>
                  <Link2 size={compact ? 20 : 24} /> {zxBusy ? "…" : "TEST UNLOCK (CAN)"}
                </button>
                {firmware && firmware.bytes && firmware.bytes.length === 0x200000 && (
                  <button data-testid="honda-zx25r-write" onClick={handleKawasakiZx} disabled={flashing || zxBusy}
                    title="Kawasaki ZX-25R flash via CAN (Tactrix Openport) — tidak perlu CONNECT K-Line dulu"
                    className={`rounded border-0 bg-gradient-to-r from-lime-400 to-green-500 hover:from-lime-300 hover:to-green-400 text-slate-950 font-bold inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed ${compact ? "flex-1 min-h-[56px] text-[14px] gap-2" : "flex-1 min-h-[64px] text-[15px] sm:text-[17px] gap-2 sm:gap-3"}`}>
                    <HardDriveDownload size={compact ? 20 : 24} /> {zxBusy ? `ZX-25R ${progress}%` : "WRITE ZX-25R (CAN)"}
                  </button>
                )}
                </>)}
              </div>
            </div>
          </div>
        </div>

        {/* Process status */}
        <div className={`rounded-lg border border-red-900/50 bg-[#0c0e14] shadow-lg shadow-black/30 flex items-center gap-4 shrink-0 relative overflow-hidden ${compact ? "px-3 py-1.5" : "px-6 py-3.5"}`} data-testid="honda-speed-bar">
          <span aria-hidden className="absolute right-0 bottom-0 h-full w-5 bg-red-600" style={{ clipPath: "polygon(100% 20%, 100% 100%, 0 100%)" }} />
          <span className={`font-black uppercase text-white shrink-0 ${compact ? "text-[13px] max-w-[130px]" : "text-[21px] max-w-[220px]"} truncate`} data-testid="honda-proc-status">{procStatus || "Process:"}</span>
          <div className={`flex-1 rounded-sm bg-[#1a1d26] border border-white/10 overflow-hidden ${compact ? "h-3" : "h-[22px]"}`}>
            <div className="h-full bg-gradient-to-r from-[#9e1414] via-[#e52321] to-[#ff5a57] shadow-[0_0_10px_rgba(229,35,33,0.6)] transition-[width] duration-300" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
          </div>
          <span className={`font-black text-white shrink-0 min-w-[76px] whitespace-nowrap text-right ${compact ? "text-[13px]" : "text-[21px]"}`} data-testid="honda-speed-pct">{Math.round(progress)} %</span>
          {speed > 0 && <span className="text-[16px] text-slate-300 shrink-0 whitespace-nowrap pr-5">{speed.toFixed(1)} KB/s</span>}
        </div>
        </>
        )}

        {tab === "dtc" && (
          <div className={`grid grid-cols-1 grid-cols-4 flex-1 min-h-0 [.ft-narrow_&]:min-h-[340px] ${compact ? "gap-2" : "gap-4"}`} data-testid="honda-dtc-panel">
            <div className={`col-span-3 h-full rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_8px_24px_rgba(0,0,0,0.5)] overflow-auto ${compact ? "min-h-0" : "min-h-[200px]"}`}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-black text-white text-[15px]">
                    <th className="px-3 py-2 w-16 border-r border-slate-600 text-center">No</th>
                    <th className="px-3 py-2 w-40 border-r border-slate-600">Code</th>
                    <th className="px-3 py-2 border-r border-slate-600">Description</th>
                    <th className="px-3 py-2 w-28 border-r border-slate-600 text-center">Current</th>
                    <th className="px-3 py-2 w-28 text-center">Past</th>
                  </tr>
                </thead>
                <tbody data-testid="honda-dtc-rows">
                  {dtcRows.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 text-sm">{dtcBusy ? "Reading…" : "Press Read DTC to scan the ECU."}</td></tr>
                  ) : dtcRows.map((r) => (
                    <tr key={r.no} className="border-b border-slate-800 text-[14px]">
                      <td className="px-3 py-2 border-r border-slate-800 text-center">{r.no}</td>
                      <td className="px-3 py-2 border-r border-slate-800 font-mono font-bold text-red-600">{r.code}</td>
                      <td className="px-3 py-2 border-r border-slate-800 text-slate-300">{r.desc}</td>
                      <td className="px-3 py-2 border-r border-slate-800 text-center font-mono">{r.current != null ? r.current : ""}</td>
                      <td className="px-3 py-2 text-center font-mono">{r.past != null ? r.past : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={`flex flex-col ${compact ? "gap-2" : "gap-4"}`}>
              <button data-testid="honda-read-dtc" onClick={doReadDtc} disabled={dtcBusy} className={`shrink-0 rounded-lg bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold inline-flex items-center justify-center disabled:opacity-50 ${compact ? "h-[52px] text-[14px] gap-2" : "h-[120px] text-[18px] gap-3"}`}><ScanSearch size={compact ? 18 : 26} /> Read DTC</button>
              <button data-testid="honda-clear-dtc" onClick={doClearDtc} disabled={dtcBusy} className={`shrink-0 rounded-lg bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold inline-flex items-center justify-center disabled:opacity-50 ${compact ? "h-[52px] text-[14px] gap-2" : "h-[120px] text-[18px] gap-3"}`}><Eraser size={compact ? 18 : 26} /> Clear DTC</button>
              <button data-testid="honda-reset-ecm" onClick={doResetEcm} disabled={dtcBusy} className={`shrink-0 rounded-lg bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold inline-flex items-center justify-center disabled:opacity-50 ${compact ? "h-[52px] text-[14px] gap-2" : "h-[120px] text-[18px] gap-3"}`}><RotateCcw size={compact ? 18 : 26} /> RESET ECM</button>
            </div>
          </div>
        )}

        {tab === "engine" && (() => {
          const s = SENSORS.find((x) => x.key === engineSel) || SENSORS[0];
          const viewBtn = (id, label) => (
            <button key={id} data-testid={`engine-view-${id}`} onClick={() => switchEngineView(id)} className={`h-7 px-3 rounded-md text-[11px] font-bold border inline-flex items-center gap-1.5 transition-colors ${engineView === id ? "bg-gradient-to-b from-[#ef2b2b] to-[#c51717] border-red-600 text-white" : "bg-[#16181d] border-[#6b7280] text-slate-300 hover:bg-[#22252c]"}`}><GaugeIcon size={13} /> {label}</button>
          );
          const switcher = <div className="flex justify-end gap-2 shrink-0" data-testid="engine-view-switch">{viewBtn("dash", "GAUGE")}{viewBtn("classic", "PARAMETER")}</div>;
          if (engineView === "dash") return (
            <div className={`flex flex-col flex-1 min-h-0 ${compact ? "gap-2" : "gap-3"}`} data-testid="honda-engine-panel-dash">
              {switcher}
              <RaceDash sample={sample} peaks={peaks} connected={connected} livePoll={livePoll} ecmId={device?.ecmId} />
            </div>
          );
          return (
          <div className={`flex flex-col flex-1 min-h-0 ${compact ? "gap-2" : "gap-3"}`} data-testid="honda-engine-panel-detail">
            {switcher}
            <div className={`grid grid-cols-12 [.ft-narrow_&]:grid-cols-1 flex-1 min-h-0 [.ft-narrow_&]:min-h-[340px] ${compact ? "gap-2" : "gap-4"}`}>
              <div className="col-span-5 [.ft-narrow_&]:col-span-1 h-full [.ft-narrow_&]:min-h-[220px] rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] overflow-auto">
                <table className="w-full text-left">
                  <thead><tr className="bg-black text-white text-[14px]"><th className="px-2 py-2 w-10 text-center">No</th><th className="px-2 py-2">Items</th><th className="px-2 py-2 w-24 text-center">Value</th><th className="px-2 py-2 w-16 text-center">Unit</th><th className="px-2 py-2 w-24 text-center">Typical(*)</th></tr></thead>
                  <tbody data-testid="honda-engine-rows">{sensorRows.map((x, i) => (
                    <tr key={x.key} className="border-b border-[#2c2f36] text-[14px]"><td className="px-2 py-1.5 text-center text-white">{i + 1}</td><td className="px-2 py-1.5 text-white font-medium">{x.label}</td><td className="px-2 py-1.5 text-center font-mono font-bold text-white text-[15px]">{fmtV(sample[x.key], x.dec)}</td><td className="px-2 py-1.5 text-center text-slate-200">{x.unit}</td><td className="px-2 py-1.5 text-center text-slate-200">{x.typical}</td></tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="col-span-5 [.ft-narrow_&]:col-span-1 h-full [.ft-narrow_&]:min-h-[240px] min-h-0 rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] p-3 flex flex-col">
                <div className="flex-1 min-h-0"><ChartBox title={`H1. ${s.label}`} series={hist[engineSel] || []} yMax={s.max} /></div>
                <div className="flex items-center gap-3 mt-2"><span className="text-white font-medium whitespace-nowrap text-sm">Select value</span>
                  <select data-testid="engine-sensor-select" value={engineSel} onChange={(e) => setEngineSel(e.target.value)} className="flex-1 h-9 rounded-[3px] border border-[#6b7280] bg-[#16181d] text-white font-mono">{sensorRows.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}</select></div>
              </div>
              <div className="col-span-2 [.ft-narrow_&]:col-span-1 h-full flex flex-col gap-3">
                <div className="rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] p-3 flex flex-col gap-2">
                  <div className="text-[12px] font-black text-white uppercase tracking-wider truncate" title={s.label}>{s.label}</div>
                  <div><div className="text-slate-200 text-[13px] font-medium mb-0.5">Min</div><input value={engineMin} onChange={(e) => setEngineMin(e.target.value.replace(/[^0-9.]/g, ""))} className="w-full h-9 text-center rounded-[3px] border border-[#6b7280] bg-[#16181d] text-white font-mono font-bold" /></div>
                  <div><div className="text-emerald-300 text-[13px] font-semibold mb-0.5">Current</div><div className="h-9 flex items-center justify-center rounded-[3px] border border-[#6b7280] bg-[#16181d] font-mono font-bold text-white text-[15px]" data-testid="engine-current">{fmtV(sample[engineSel], s.dec)}</div></div>
                  <div><div className="text-slate-200 text-[13px] font-medium mb-0.5">Max</div><div className="h-9 flex items-center justify-center rounded-[3px] border border-[#6b7280] bg-[#16181d] font-mono font-bold text-white" data-testid="engine-max">{fmtV(peaks[engineSel], s.dec)}</div></div>
                </div>
                <div className={`text-center text-[13px] font-bold rounded-lg border py-2 ${connected ? (livePoll ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300") : "border-slate-700 bg-[#121824] text-slate-400"}`} data-testid="engine-live-status">
                  {connected ? (livePoll ? "● LIVE" : "CONNECTING…") : connecting ? "CONNECTING…" : "○ OFFLINE"}
                </div>
              </div>
            </div>
            <div className={`grid gap-2 shrink-0 ${compact ? "grid-cols-4" : "grid-cols-8"}`} data-testid="engine-gauges">
              {gaugeCards.map((g) => { const sd = SENSORS.find((x) => x.key === g.key); return <Gauge key={g.key} label={g.short} value={fmtV(sample[g.key], sd.dec)} unit={sd.unit} color={g.color} />; })}
            </div>
          </div>
          );
        })()}

        {tab === "tools" && (() => {
          const secAscii = securityPw || "HelloHowAreYou";
          const secHex = Array.from(secAscii).map((c) => c.charCodeAt(0).toString(16).padStart(2, "0").toUpperCase());
          const sizes = toolMaker === "Keihin" ? SIZES_KEIHIN : toolMaker === "Denso" ? SIZES_DENSO : SIZES_SHINDENGEN;
          const modeBtn = (id, label, Icon) => (
            <button key={id} data-testid={`tools-mode-${id}`} onClick={() => setToolMode(id)} disabled={reading}
              className={`rounded-lg font-bold text-[12px] border inline-flex items-center justify-center gap-2 transition ${compact ? "h-9" : "h-11"} ${toolMode === id ? "bg-gradient-to-b from-[#ef2b2b] to-[#c51717] border-red-600 text-white" : "bg-[#16181d] border-[#6b7280] text-slate-200 hover:bg-[#22252c]"} disabled:opacity-40`}><Icon size={16} /> {label}</button>
          );
          return (
          <div className={`grid grid-cols-1 grid-cols-5 grid-rows-[minmax(0,1fr)] flex-1 min-h-0 overflow-hidden [.ft-narrow_&]:overflow-visible [.ft-narrow_&]:grid-rows-none [.ft-narrow_&]:min-h-[380px] ${compact ? "gap-2" : "gap-4"}`} data-testid="honda-tools-panel">
            <div className={`col-span-3 h-full min-h-0 flex flex-col overflow-auto pr-1 order-2 ${compact ? "gap-2" : "gap-3"}`}>
              <div className={`rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] flex flex-col shrink-0 ${compact ? "p-2 gap-2" : "p-3 gap-3"}`}>
                <div className="grid grid-cols-3 gap-2">
                  {modeBtn("read", "READ / BACKUP", HardDriveDownload)}
                  {modeBtn("eeprom", "EEPROM", MemoryStick)}
                  {modeBtn("flashcount", "FLASH COUNT", Hash)}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={toolMaker} onChange={(e) => { setToolMaker(e.target.value); if (e.target.value === "Denso") setReadSize("384 KB"); }} data-testid="tools-maker-select" className={`px-3 rounded-[3px] border border-[#6b7280] bg-[#16181d] text-white text-[13px] font-bold outline-none focus:border-red-500 ${compact ? "h-8" : "h-10"}`}>
                    <option>Shindengen</option><option>Keihin</option>{SHOW_DENSO_READ && <option>Denso</option>}
                  </select>
                  <span className={`text-[12px] font-bold px-2 py-1 rounded ${connected ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700/40 text-slate-400"}`}>{connected ? "● CONNECTED" : "○ OFFLINE"}</span>
                  <button data-testid="tools-connect" onClick={onConnectBtn} className={`ml-auto px-4 rounded-lg border font-bold text-[13px] inline-flex items-center gap-2 transition ${compact ? "h-8" : "h-10"} ${(connected || connecting || reading || flashing) ? "border-red-600/50 bg-transparent hover:bg-red-600/10 text-red-400" : "border-red-600 bg-red-600 hover:bg-red-700 text-white"}`}>{(connected || connecting || reading || flashing) ? <Power size={16} /> : <Link2 size={16} />}{(reading || flashing || connected) ? "DISCONNECT" : connecting ? "CONNECTING… · STOP" : "CONNECT"}</button>
                </div>
              </div>
              {coldGuide.open && <div data-i18n-free><ColdGuideBanner phase={coldGuide.phase} seconds={coldGuide.seconds} /></div>}

              {toolMode === "read" && (
                <div className={`rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] flex flex-col shrink-0 ${compact ? "p-2 gap-1.5" : "p-4 gap-3"}`} data-testid="tools-read-panel">
                  <div className="flex items-center gap-2 text-white font-black uppercase tracking-wide text-[15px]"><HardDriveDownload size={18} className="text-red-500" /> Read / Backup ECU</div>
                  <div className="text-[12px] text-slate-300">DUMP SIZE <span className="text-red-400 font-bold">{toolMaker} · {readSize}</span></div>
                  <div className={`grid ${sizes.length <= 2 ? "grid-cols-2" : "grid-cols-3"} ${compact ? "gap-1.5" : "gap-2"}`}>
                    {sizes.map((s) => (<button key={s} data-testid={`tools-size-${s.replace(/\s/g, "")}`} onClick={() => setReadSize(s)} className={`rounded-lg font-bold text-[13px] border transition ${compact ? "h-8" : "h-11"} ${readSize === s ? "bg-gradient-to-b from-[#ef2b2b] to-[#c51717] border-red-600 text-white" : "bg-[#16181d] border-[#6b7280] text-slate-200 hover:bg-[#22252c]"}`}>{s}</button>))}
                  </div>
                  {toolMaker === "Keihin" && (
                    <div className={`rounded-md border border-[#4b505b] bg-[#16181d] ${compact ? "p-2" : "p-3"}`}>
                      <div className="flex items-center justify-between mb-2"><span className="text-[12px] font-bold text-slate-200">🔒 SECURITY ID (KEIHIN)</span>
                        <button onClick={() => setSecurityPw("")} className="text-[11px] text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1"><RotateCcw size={12} /> Reset</button></div>
                      <div className="grid grid-cols-7 gap-1 mb-1">{secHex.slice(0, 7).map((h, i) => (<div key={i} className={`rounded bg-[#0c1017] border border-[#4b505b] flex items-center justify-center text-[12px] font-mono text-white ${compact ? "h-6" : "h-8"}`}>{h}</div>))}</div>
                      <div className="grid grid-cols-7 gap-1">{secHex.slice(7, 14).map((h, i) => (<div key={i} className={`rounded bg-[#0c1017] border border-[#4b505b] flex items-center justify-center text-[12px] font-mono text-white ${compact ? "h-6" : "h-8"}`}>{h}</div>))}</div>
                      <div className="mt-2 text-[12px] text-slate-400">ASCII <input data-testid="tools-security-pw" value={securityPw} onChange={(e) => setSecurityPw(e.target.value)} placeholder="HelloHowAreYou" className="ml-1 w-48 h-7 px-2 rounded-[3px] bg-[#0c1017] border border-[#6b7280] text-white font-mono text-[12px] outline-none focus:border-red-500" /></div>
                    </div>
                  )}
                  {toolMaker !== "Denso" && <button data-testid="tools-read-flash" onClick={doReadFlash} disabled={reading} className={`rounded-lg bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold text-[16px] inline-flex items-center justify-center gap-3 disabled:opacity-50 ${compact ? "h-9" : "h-12"}`}><HardDriveDownload size={20} /> Read {readSize}</button>}
                  {toolMaker === "Denso" && (<>
                    <div data-testid="tools-denso-note" className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-[12px] text-amber-200">Denso Titan 384KB (RH850): READ penuh belum terbukti. Buka BIN referensi 384KB, Connect, lalu jalankan Probe Read Mode Aplikasi (tanpa bootloader, tanpa erase/write). Read 384KB aktif hanya jika probe menemukan data yang cocok.</div>
                    <button data-testid="tools-denso-app-probe" onClick={handleDensoAppProbe} disabled={reading || flashing} className={`rounded-lg bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-50 ${compact ? "h-9" : "h-12"}`}><ScanSearch size={18} /> Probe Read Mode Aplikasi (tanpa bootloader)</button>
                    <button data-testid="tools-denso-app-read" onClick={handleDensoAppRead} disabled={reading || flashing || !densoMethod} title={densoMethod ? "" : "Aktif setelah Probe Read Mode Aplikasi menemukan data cocok"} className={`rounded-lg border border-red-600 bg-[#16181d] hover:bg-[#22252c] text-white font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-40 ${compact ? "h-9" : "h-12"}`}><HardDriveDownload size={18} /> Read 384KB (mode aplikasi)</button>
                    <button data-testid="tools-denso-read-probe" onClick={handleDensoProbe} disabled={reading || flashing} className={`rounded-lg bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-50 ${compact ? "h-9" : "h-12"}`}><ScanSearch size={18} /> Probe Read Denso (tanpa erase)</button>
                  </>)}
                </div>
              )}
              {/* Sahara Denso read panel disembunyikan sementara (read K3H belum didukung) */}
              {false && toolMode !== "eeprom" && (
                <SaharaReadPanel manager={manager} log={log} onProgress={(_ph, p) => setProgress(p)} disabled={reading || flashing}
                  ensureBridge={async () => { if (!manager.bridge) await manager.connectBridge({ url: BRIDGE_URL, baud: BAUD, dll: "tactrix" }); }} />
              )}

              {toolMode !== "read" && toolMaker === "Denso" && (
                <div data-testid="tools-denso-unsupported" className="rounded-md border-2 border-[#4b505b] bg-[#111318] p-4 text-[13px] text-slate-300">EEPROM / Flash Count untuk Denso belum didukung.</div>
              )}
              {toolMode === "eeprom" && toolMaker !== "Denso" && (
                <div className={`rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] flex flex-col shrink-0 ${compact ? "p-2.5 gap-2" : "p-4 gap-3"}`} data-testid="tools-eeprom-panel">
                  <div className="flex items-center gap-2 text-white font-black uppercase tracking-wide text-[15px]"><MemoryStick size={18} className="text-red-500" /> EEPROM Tools · {toolMaker}</div>
                  <div className="flex items-center gap-2" data-testid="tools-eeprom-size-row">
                    <span className="text-[13px] text-slate-400 shrink-0">Size EEPROM</span>
                    <select value={eepromSize} onChange={(e) => setEepromSize(Number(e.target.value))} disabled={reading}
                      data-testid="tools-eeprom-size" className="flex-1 h-9 rounded-[3px] border border-[#6b7280] bg-[#16181d] px-2 text-[14px] text-white outline-none focus:border-red-500 disabled:opacity-50">
                      {(toolMaker === "Keihin" ? [512] : [512, 1024]).map((s) => <option key={s} value={s}>{s} B</option>)}
                    </select>
                    {toolMaker === "Keihin" && <span className="text-[11px] text-slate-500 shrink-0">K93 read = 512 B</span>}
                  </div>
                  <button data-testid="tools-read-eeprom" onClick={doReadEeprom} disabled={reading} className="h-11 rounded-lg bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] text-white font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-50"><MemoryStick size={18} /> Read EEPROM</button>
                  {toolMaker === "Keihin" && (
                    <div className="grid grid-cols-2 gap-2">
                      <button data-testid="tools-format-ff" onClick={() => doResetEeprom("ff")} disabled={reading} className="h-11 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-bold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-50"><Eraser size={16} /> Format → 0xFF</button>
                      <button data-testid="tools-format-00" onClick={() => doResetEeprom("00")} disabled={reading} className="h-11 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-bold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-50"><Eraser size={16} /> Format → 0x00</button>
                    </div>
                  )}
                  <div className="rounded-lg border border-white/10 bg-[#121824] p-3 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-slate-400">WRITE SOURCE</span>
                      <input ref={eepromRef} type="file" accept=".bin" className="hidden" data-testid="tools-eeprom-input" onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleOpenEeprom(f); if (e.target) e.target.value = ""; }} />
                      <button data-testid="tools-open-eeprom" onClick={() => eepromRef.current?.click()} className="h-8 px-3 rounded border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 font-bold text-[12px] inline-flex items-center gap-1"><Upload size={14} /> Open File EEPROM</button>
                    </div>
                    <div className="text-[12px] text-slate-400 font-mono">{eepromName ? `${eepromName} · ${eepromBytes?.length || 0} B` : "No source — open a 256/512/1024 B .bin or Read first."}</div>
                  </div>
                  <button data-testid="tools-write-eeprom" onClick={doWriteEeprom} disabled={reading || !eepromBytes} className="h-11 rounded-lg border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-300 font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-40"><Upload size={18} /> Write EEPROM</button>
                </div>
              )}

              {toolMode === "flashcount" && toolMaker !== "Denso" && (
                <div className={`rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] flex flex-col shrink-0 ${compact ? "p-2.5 gap-2" : "p-4 gap-3"}`} data-testid="tools-flashcount-panel">
                  <div className="flex items-center gap-2 text-white font-black uppercase tracking-wide text-[15px]"><Hash size={18} className="text-red-500" /> Flash Count · {toolMaker}</div>
                  <div className="text-[13px] text-slate-400">Reset / clear penghitung flash pada ECU. Butuh koneksi & ECU aman.</div>
                  <button data-testid="tools-flash-count" onClick={doFlashCount} disabled={reading} className={`rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-[16px] inline-flex items-center justify-center gap-2 disabled:opacity-50 ${compact ? "h-10" : "h-12"}`}><Hash size={20} /> Reset Flash Count</button>
                </div>
              )}

              <div className={`rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] flex items-center gap-4 shrink-0 mt-auto ${compact ? "px-3 py-1.5" : "px-4 py-3"}`}>
                <span className={`text-[15px] font-black uppercase shrink-0 truncate text-white ${procStatus ? "max-w-[200px]" : ""}`} data-testid="tools-proc-status">{procStatus || "Progress:"}</span>
                <div className={`flex-1 rounded-sm bg-[#1a1d26] border border-white/10 overflow-hidden ${compact ? "h-3" : "h-5"}`}><div className="h-full bg-gradient-to-r from-[#9e1414] via-[#e52321] to-[#ff5a57] shadow-[0_0_10px_rgba(229,35,33,0.6)] transition-[width] duration-300" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} /></div>
                <span className="text-[15px] font-black text-white shrink-0 w-16 text-right" data-testid="tools-progress-pct">{Math.round(progress)} %</span>
              </div>
            </div>

            <div className={`col-span-2 h-full flex flex-col min-h-0 order-1 ${compact ? "gap-2" : "gap-3"}`}>
              <div className={`rounded-md border-2 border-[#4b505b] bg-gradient-to-b from-[#111318] to-[#0b0c10] shrink-0 ${compact ? "p-2" : "p-3"}`} data-testid="tools-target">
                <div className="flex items-center gap-2 text-white font-black uppercase tracking-wide text-[14px] mb-2"><Cpu size={16} className="text-red-500" /> Target</div>
                {[["Connection", connected ? "Native bridge" : "No Interface"], ["ECM / Chip ID", d.ecmId || "—"], ["Part Number", (d.origPart || d.part || "—").split(" · ")[0]], ["ECU Type", d.maker || "—"], ["MCU Type", d.chip || "—"], ["Flash Count", d.flashCount || "—"]].map(([k, v]) => (
                  <div key={k} className={`flex items-center justify-between border-b border-[#2c2f36] last:border-0 text-[13px] ${compact ? "py-0.5" : "py-1"}`}><span className="text-slate-200">{k}</span><span className="font-mono text-white">{v}</span></div>
                ))}
              </div>
              <div className={`relative flex-1 min-h-0 ${compact ? "min-h-[80px]" : "min-h-[160px]"}`}>
                <LogView boxRef={toolsLogRef} lines={logLines} empty={logText} testid="tools-console" className="rounded-sm !bg-[#111317] !border-[#6b7280]" />
                <div className="absolute top-2 right-3 flex items-center gap-1.5">
                  <button data-testid="tools-copy-log" onClick={copyLog} title="Copy log" className="h-8 w-8 rounded-[3px] bg-[#1d2027] hover:bg-[#2a2e37] border border-[#6b7280] text-white inline-flex items-center justify-center"><Copy size={15} /></button>
                  <button data-testid="tools-clear-log" onClick={clearLog} title="Hapus isi log" className="h-8 w-8 rounded-[3px] bg-red-700 hover:bg-red-600 text-white inline-flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        </div>

        {/* Bottom tabs */}
        <div className="relative grid grid-cols-4 [.ft-narrow_&]:grid-cols-2 gap-0 rounded-lg overflow-hidden border border-red-900/50 bg-[#0c0e14] shrink-0" data-testid="honda-bottom-tabs">
          <span aria-hidden className="pointer-events-none absolute right-0 bottom-0 h-full w-6 bg-red-600 z-10" style={{ clipPath: "polygon(100% 30%, 100% 100%, 0 100%)" }} />
          {BOTTOM_TABS.map((t) => { const TabIcon = TAB_ICONS[t.id]; return (
            <button key={t.id} data-testid={`honda-tab-${t.id}`} disabled={flashing || reading}
              onClick={() => { if (flashing || reading) { toast.error("Proses WRITE sedang berjalan — tidak bisa pindah tab", { description: "Tunggu sampai selesai." }); return; } setTab(t.id); }}
              style={tab === t.id ? { clipPath: "polygon(0 0, 100% 0, calc(100% - 18px) 100%, 0 100%)" } : undefined}
              className={`relative px-2 inline-flex items-center justify-center gap-2 text-[11px] sm:text-[14px] leading-tight font-medium tracking-wide uppercase transition-colors ${compact ? "min-h-[38px] py-1" : "min-h-[52px] py-1.5"} ${tab === t.id ? "bg-gradient-to-r from-red-700 to-red-600 text-white" : "bg-transparent text-slate-300 hover:bg-white/5 hover:text-white"} ${(flashing || reading) && tab !== t.id ? "opacity-40 cursor-not-allowed" : ""}`}>
              {TabIcon && <TabIcon size={compact ? 16 : 21} className="shrink-0" />}
              <span>{t.label}</span>
              {tab !== t.id && <span aria-hidden className="absolute right-0 top-2 bottom-2 w-px bg-white/15 -skew-x-[25deg]" />}
            </button>
          ); })}
        </div>
      </div>

      {saveDlg && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="honda-saveas-dialog">
          <div className="w-full max-w-md rounded-xl border border-slate-700 bg-[#0c1017] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <HardDriveDownload className="h-5 w-5 text-cyan-400" />
              <h3 className="font-bold text-white uppercase text-base">Save Read As…</h3>
              <button data-testid="honda-saveas-close" onClick={() => setSaveDlg(null)} className="ml-auto p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">Nama tambahan (setelah nama dasar)</label>
            <div className="flex items-center rounded-lg border border-slate-700 bg-[#121824] overflow-hidden">
              <span className="px-2.5 py-2 text-sm font-mono text-slate-500 border-r border-slate-700 truncate max-w-[55%]" title={saveDlg.base}>{saveDlg.base}</span>
              <input data-testid="honda-saveas-input" autoFocus value={saveNameSuffix} onChange={(e) => setSaveNameSuffix(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doSaveConfirm(); if (e.key === "Escape") setSaveDlg(null); }}
                placeholder="_backup" className="flex-1 min-w-0 bg-transparent px-2 py-2 text-sm font-mono text-slate-100 outline-none" />
            </div>
            <div className="mt-3 text-[11px] text-slate-500">File akhir: <span data-testid="honda-saveas-preview" className="font-mono text-emerald-400">{`${saveDlg.base}${saveNameSuffix || ""}.bin`}</span></div>
            <p className="mt-2 text-[11px] text-slate-600">Folder dipilih di dialog berikutnya (browser/app yang mendukung). Jika tidak, file diunduh ke folder default.</p>
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button data-testid="honda-saveas-cancel" onClick={() => setSaveDlg(null)} className="px-4 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold text-sm">Batal</button>
              <button data-testid="honda-saveas-confirm" onClick={doSaveConfirm} className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950 font-bold text-sm"><Download className="h-4 w-4" /> Choose folder & Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </CompactCtx.Provider>
  );
}

// Default export: embedded keeps the panel as-is; standalone page uses the same full-screen frame as Map Editor.
export default function HondaFlashtools({ embedded = false, onClose }) {
  const navigate = useNavigate();
  if (embedded) return <HondaFlashtoolsPanel embedded onClose={onClose} />;
  return <FitFrame onClose={() => navigate(window.tiroDesktopApp ? "/workspace" : "/")} />;
}

const BASE_W = 1440, BASE_H = 880;
const IS_ANDROID = typeof navigator !== "undefined" && /Android|TiroTunerApp/i.test(navigator.userAgent || "");
const isJ = (d) => d === "tactrix" || d === "sm3";
const SM_NAMES = { sm3: "SM3 USB", sm3wifi: "SM3 Wi-Fi", sm2: "SM2 USB" };
const calcFit = () => {
  const vw = window.innerWidth, vh = window.innerHeight;
  const narrow = vw < 820 && vh > vw;
  // Android: full screen (fills the viewport, no floating card margin).
  const scale = narrow ? 1 : IS_ANDROID ? Math.min(vw / BASE_W, vh / BASE_H) : Math.min(1.15, (vw * 0.88) / BASE_W, (vh * 0.9) / BASE_H);
  return { vw, vh, narrow, scale };
};

export function FitFrame({ onClose }) {
  const [fit, setFit] = useState(calcFit);
  useEffect(() => {
    const on = () => setFit(calcFit());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);
  const full = IS_ANDROID && !fit.narrow;
  const style = fit.narrow ? undefined : full ? { width: fit.vw / fit.scale, height: fit.vh / fit.scale, zoom: fit.scale } : { width: BASE_W, height: BASE_H, zoom: fit.scale };
  const box = fit.narrow ? "w-screen min-h-[100dvh] rounded-none" : full ? "rounded-none overflow-hidden border-0" : "rounded-xl overflow-hidden ring-1 ring-red-600/80 shadow-[0_0_10px_rgba(239,43,43,0.55),0_30px_80px_rgba(0,0,0,0.8)]";
  return (
    <div className={`fixed inset-0 z-[200] ${full ? "bg-[#0F1015]" : "bg-black/80 backdrop-blur-sm"} ${fit.narrow ? "ft-narrow overflow-y-auto p-0" : "flex items-center justify-center"}`} data-testid="flashtools-page-frame">
      <div style={style} className={`relative border border-white/10 bg-[#0F1015] shadow-2xl ${box}`}>
        <HondaFlashtoolsPanel embedded onClose={onClose} />
      </div>
    </div>
  );
}

